// kanjo/Menus.jsx — Second-level menu pages for Receipts, Buy, Reports.
// Tiles render with live counts/sums from Dexie; Reports period selector binds
// to ?period= in the hash so drill-ins inherit the context.

// Status-dot palette for tile indicators. Dots are decorative (the label
// carries the information); labels stay on --ink-2 per the pairing rules.
const TILE_DOT = {
  green: 'var(--positive)', amber: 'var(--warning)', red: 'var(--danger)', muted: 'var(--ink-3)',
};

function MenuTile({ icon, iconColor, iconBg, title, subtitle, indicators, badge, to }) {
  return (
    <KLink to={to} asButton style={{ display: 'block', height: '100%' }}>
    <div className="card" style={{
      padding: 18,
      display: 'flex', flexDirection: 'column', gap: 14,
      minHeight: 138, height: '100%', boxSizing: 'border-box',
      position: 'relative',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 12,
        background: iconBg, color: iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>{title}</div>
        {indicators !== undefined ? (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3, minHeight: 18 }}>
            {indicators == null
              ? <span aria-hidden="true" style={{ display: 'inline-block', width: 80, height: 10, borderRadius: 4, background: 'var(--surface-3)' }}/>
              : indicators.map((it, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.35, minWidth: 0 }}>
                    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: TILE_DOT[it.tone] || TILE_DOT.muted }}/>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
                  </div>
                ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.35, minHeight: 18 }}>
            {subtitle == null
              ? <span aria-hidden="true" style={{ display: 'inline-block', width: 80, height: 10, borderRadius: 4, background: 'var(--surface-3)' }}/>
              : subtitle}
          </div>
        )}
      </div>
      {badge ? (
        <div style={{
          position: 'absolute', top: 18, right: 18,
          minWidth: 22, height: 22, padding: '0 7px',
          borderRadius: 11, background: 'var(--danger-fill)', color: 'var(--on-danger-fill)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)',
        }}>{badge}</div>
      ) : null}
    </div>
    </KLink>
  );
}

function MenuGrid({ children }) {
  return (
    <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}

// Icon set
const Ico = {
  scan: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2.5"/><circle cx="12" cy="13" r="3.4"/><path d="M8 6l1.4-2.4h5.2L16 6"/></svg>,
  list: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg>,
  flag: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 13H3z"/><path d="M12 9v3M12 14v.5"/></svg>,
  calendar: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2.5"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>,
  cart: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h2.5l2 11.5a2 2 0 002 1.5h8a2 2 0 002-1.5L21 9H7"/><circle cx="10" cy="21" r="1.3"/><circle cx="17" cy="21" r="1.3"/></svg>,
  repeat: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9a8 8 0 0114-3M20 5v4h-4M20 15a8 8 0 01-14 3M4 19v-4h4"/></svg>,
  tag: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12V4h8l9 9-8 8z"/><circle cx="8" cy="8" r="1.3"/></svg>,
  bell: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 16h14l-2-3v-3a5 5 0 00-10 0v3z"/><path d="M10 20a2 2 0 004 0"/></svg>,
  storefront: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10v9h16v-9"/><path d="M3 6h18l-1 4a3 3 0 01-5 1 3 3 0 01-5 0 3 3 0 01-5-1z"/></svg>,
  trendUp: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-7 4 4 7-9"/><path d="M14 5h6v6"/></svg>,
  gauge: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17a8 8 0 1116 0"/><path d="M12 17l4-6"/><circle cx="12" cy="17" r="1.3" fill="currentColor"/></svg>,
  clock: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  link: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14a3.5 3.5 0 005 0l3-3a3.5 3.5 0 00-5-5l-1 1"/><path d="M14 10a3.5 3.5 0 00-5 0l-3 3a3.5 3.5 0 005 5l1-1"/></svg>,
  bank: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-5 9 5"/><path d="M5 10v8M10 10v8M14 10v8M19 10v8M3 19h18"/></svg>,
  wave: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 14c3 0 3-5 6-5s3 5 6 5 3-5 6-5"/><path d="M3 19h18"/></svg>,
  search: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>,
  upload: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 16v2a2 2 0 002 2h10a2 2 0 002-2v-2"/><path d="M12 3v12M7 8l5-5 5 5"/></svg>,
  keyboard: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="6" width="19" height="13" rx="2.2"/><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h.01M18 14h.01M9.5 14h5"/></svg>,
  plus: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>,
};

// ─── Shared count hook ──────────────────────────────────────
// Pulls live values needed by all three menus in a single pass.
function useMenuCounts(period) {
  const [c, setC] = React.useState({ loading: true });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setC({ loading: false }); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        const today = localToday();
        const monthStart = today.slice(0, 8) + '01';
        const { from, to } = periodRange(today, period);

        const profile = await db.userProfile.get(userId);
        const currency = profile?.currency || 'USD';
        const locale = profile?.locale || 'en-US';

        // Best-effort parallel fetch. Each query is wrapped in catch so a single
        // missing index/table doesn't break the whole menu.
        const safe = (p) => p.catch(() => null);

        const [
          openInvoices,
          paidMonth,
          alertsOpen,
          belowParCount,
          openPOs,
          activeVendors,
          salesPeriod,
          cogsPeriod,
          priceMovers,
        ] = await Promise.all([
          safe(db.invoices.where('[userId+status]').equals([userId, 'RECEIVED']).toArray()),
          safe(db.invoices.where('[userId+status]').equals([userId, 'PAID_LOCAL']).filter((i) => (i.issuedDate || '') >= monthStart).toArray()),
          safe(db.alerts.where('[userId+dismissed]').equals([userId, 0]).toArray()),
          safe(window.kanjo.procurement.orderGuide.belowParCount?.() ?? Promise.resolve(0)),
          safe(db.purchaseOrders.where('[userId+status]').equals([userId, 'SUBMITTED']).count()),
          safe(db.vendors.where('userId').equals(userId).filter((v) => v.isActive === 1).count()),
          safe(db.salesEntries.where('userId').equals(userId).filter((e) => e.date >= from && e.date <= to).toArray()),
          safe(window.kanjo.revenue.cogs.calc({ from: addDaysIso(today, -13), to: today })),
          safe(computePriceMovers(db, userId)),
        ]);

        const billsDueCents = (openInvoices || []).reduce((s, i) => s + (i.totalCents || 0), 0);
        const paidMonthCents = (paidMonth || []).reduce((s, i) => s + (i.totalCents || 0), 0);
        const salesPeriodCents = (salesPeriod || []).reduce((s, e) => s + (e.netSalesCents || 0), 0);
        const reviewCount = (alertsOpen || []).filter((a) => /DUPLICATE|VARIANCE|OCR_FAILED|UNMATCHED/i.test(a.kind || '')).length;
        const priceAlertCount = (alertsOpen || []).filter((a) => /PRICE/i.test(a.kind || '')).length;

        if (!cancelled) setC({
          loading: false, currency, locale, period,
          reviewCount,
          billsDueCents,
          paidMonthCents,
          belowParCount: belowParCount || 0,
          openPOs: openPOs || 0,
          priceAlertCount,
          activeVendors: activeVendors || 0,
          salesPeriodCents,
          foodCostPct14d: cogsPeriod && cogsPeriod.foodCostPct != null ? cogsPeriod.foodCostPct * 100 : null,
          foodCostStatus: (cogsPeriod && cogsPeriod.status) || null, // 'green'|'amber'|'red'|'no-sales'
          cogs14dCents: (cogsPeriod && cogsPeriod.cogsCents) || 0,
          priceMoversCount: (priceMovers?.rows || []).length,
          // Headline pct is the top CLEAN mover — suspect (>±50%) moves are held
          // for review and must never inflate the tile into "top 2,082%".
          priceMoversMaxPct: (priceMovers?.rows || []).reduce((m, r) => Math.max(m, Math.abs(r.pct || 0)), 0),
          priceReviewCount: (priceMovers?.review || []).length,
        });
      } catch (e) {
        console.warn('[Menus] counts load failed', e);
        if (!cancelled) setC({ loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [period]);
  return c;
}

function periodRange(today, period) {
  if (period === 'today') return { from: today, to: today };
  if (period === 'month') {
    const start = today.slice(0, 8) + '01';
    return { from: start, to: today };
  }
  // default: week (last 7 days inclusive)
  const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 6);
  return { from: d.toISOString().slice(0, 10), to: today };
}

function addDaysIso(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtMoneyM(cents, currency, locale) {
  if (cents == null) return '—';
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  const v = (c || 0) / 100;
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 1, notation: 'compact' }).format(v);
  }
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
}

// Exact money — always 2 decimals, never compact. Use anywhere precise amounts
// matter (receipt line items, unit prices, totals).
function fmtMoneyExact(cents, currency, locale) {
  if (cents == null) return '—';
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((c || 0) / 100);
}

// ─── Read query string from hash route ──────────────────────
function useHashQuery(key) {
  const nav = React.useContext(KanjoNav);
  const route = nav?.current || '';
  return React.useMemo(() => {
    const q = route.split('?')[1];
    if (!q) return null;
    const params = new URLSearchParams(q);
    return params.get(key);
  }, [route, key]);
}

function setHashQuery(basePath, params) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') usp.set(k, v);
  }
  const q = usp.toString();
  window.location.hash = q ? `${basePath}?${q}` : basePath;
}

// ─── Receipts menu ──────────────────────────────────────────
// Receipts home data: receipt rows (90d, with vendor + line count), vendor-spend
// rollup (this month vs last + 3-month trend), bills-due total, and the supplier
// list for the filter. One pass over invoices + a single lineItems load.
function useReceiptsHome() {
  const [st, setSt] = React.useState({ loading: true, rows: [], vendors: [], vendorList: [], billsDueCents: 0, curTotal: 0, lastTotal: 0, m0: '', m1: '', currency: 'USD', locale: 'en-US' });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt((s) => ({ ...s, loading: false })); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        const today = localToday();
        const profile = await db.userProfile.get(userId).catch(() => null);
        const invoicesAll = (await db.invoices.where('userId').equals(userId).toArray()).filter((i) => !i.isDuplicate);

        const vmap = {}, vmethod = {};
        for (const vid of [...new Set(invoicesAll.map((i) => i.vendorId).filter(Boolean))]) {
          // vmap holds DISPLAY names only — every lookup and grouping below keys
          // on vendorId, so routing through vendorName() changes the label and
          // nothing else.
          const v = await db.vendors.get(vid); if (v) { vmap[vid] = vendorName(v.name); vmethod[vid] = v.paymentMethod || null; }
        }
        const allLines = await db.lineItems.where('userId').equals(userId).toArray();
        const lineCount = {};
        for (const l of allLines) lineCount[l.invoiceId] = (lineCount[l.invoiceId] || 0) + 1;
        const dateOf = (i) => i.issuedDate || (i.createdAt ? new Date(i.createdAt).toISOString().slice(0, 10) : '');

        // Vendor spend: this month (m0) vs last (m1), with a 3-month trend each.
        const m0 = today.slice(0, 7), m1 = shiftYm(m0, -1), months3 = [shiftYm(m0, -2), m1, m0];
        const byVendor = {}; let curTotal = 0, lastTotal = 0, ccSpendCents = 0;
        for (const i of invoicesAll) {
          const ym = ymOf(i.issuedDate); if (!ym) continue;
          const vid = i.vendorId || '__none'; const amt = i.totalCents || 0;
          (byVendor[vid] = byVendor[vid] || {})[ym] = (byVendor[vid][ym] || 0) + amt;
          if (ym === m0) { curTotal += amt; if (vmethod[vid] === 'card') ccSpendCents += amt; }
          else if (ym === m1) lastTotal += amt;
        }
        const vendors = Object.keys(byVendor).map((vid) => {
          const mm = byVendor[vid]; const cur = mm[m0] || 0, last = mm[m1] || 0;
          return { vendorId: vid, name: vid === '__none' ? 'Unknown vendor' : (vmap[vid] || 'Unknown vendor'),
            cur, last, pct: last > 0 ? ((cur - last) / last) * 100 : null, isNew: last === 0 && cur > 0,
            months: months3.map((ym) => ({ ym, cents: mm[ym] || 0 })) };
        }).filter((v) => v.cur > 0 || v.last > 0).sort((a, b) => b.cur - a.cur);

        // Receipt rows, last 90 days, newest first.
        const cutoff = (() => { const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 90); return d.toISOString().slice(0, 10); })();
        const rows = invoicesAll.filter((i) => dateOf(i) >= cutoff)
          .map((i) => ({ ...i, vendorName: vmap[i.vendorId] || 'Unknown vendor', lineCount: lineCount[i.invoiceId] || 0, _d: dateOf(i) }))
          .sort((a, b) => a._d !== b._d ? (a._d < b._d ? 1 : -1) : (b.createdAt || 0) - (a.createdAt || 0));

        let billsDueCents = 0;
        try { const u = await window.kanjo.cashflow.payments.unpaidSummary(today); billsDueCents = u.totalCents || 0; } catch { /* none */ }
        // What the last closed statement period cost (City Produce & friends) —
        // the natural companion to "Outstanding" for deferred-payment vendors.
        let lastPeriodPaid = null;
        try {
          const lp = await window.kanjo.cashflow.payments.lastPeriodPaidSummary(today);
          if (lp && lp.totalCents > 0) lastPeriodPaid = lp; // full detail feeds the drill-in
        } catch { /* none */ }

        const vendorList = Array.from(new Map(rows.map((r) => [r.vendorId, r.vendorName])).entries()).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

        // Rolling 30-day total for the "Last 30 days" period tab — a window
        // that doesn't reset on the 1st, unlike curTotal/lastTotal (calendar
        // months). Drawn from `rows`, which already covers the last 90 days.
        const last30Cutoff = (() => { const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 29); return d.toISOString().slice(0, 10); })();
        const last30Total = rows.filter((r) => r._d >= last30Cutoff).reduce((s, r) => s + (r.totalCents || 0), 0);

        if (!cancelled) setSt({ loading: false, rows, vendors, vendorList, billsDueCents, lastPeriodPaid, ccSpendCents, curTotal, lastTotal, last30Total, m0, m1,
          currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) { console.warn('[receipts] load failed', e); if (!cancelled) setSt((s) => ({ ...s, loading: false })); }
    })();
    return () => { cancelled = true; };
  }, []);
  return st;
}

// One receipt line inside a week card. Chev signals it drills in to detail.
function ReceiptRowItem({ r, M, last }) {
  return (
    <KLink to={`/receipts/detail?id=${encodeURIComponent(r.invoiceId)}`} asButton style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: last ? 'none' : '1px solid var(--hairline)' }}>
        <div style={{ minWidth: 0, paddingRight: 12 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.vendorName}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{r._d}{r.lineCount ? ` · ${r.lineCount} item${r.lineCount === 1 ? '' : 's'}` : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{M(r.totalCents || 0)}</div>
          <Chev/>
        </div>
      </div>
    </KLink>
  );
}

function ReceiptsMenu({ dark = false, persona = 'owner' }) {
  useT();
  const st = useReceiptsHome();
  const [sel, setSel] = React.useState(null);          // expanded vendor (spend trend)
  const [supplier, setSupplier] = React.useState('all'); // receipts supplier filter
  const [sideTab, setSideTab] = React.useState('bills'); // 'bills' | 'cc'
  const [paidOpen, setPaidOpen] = React.useState(false); // "Previous period paid" drill-in
  const [period, setPeriod] = React.useState('month');  // 'month' | 'last30' | 'lastMonth' — headline total window
  const [addOpen, setAddOpen] = React.useState(false);  // (+) sheet: scan / upload / enter manually
  const M = (c) => fmtMoneyM(c, st.currency, st.locale);
  const sideValue = sideTab === 'bills' ? st.billsDueCents : st.ccSpendCents;
  const sideTo = sideTab === 'bills' ? '/receipts/bills' : '/buy/vendor';
  const periodTotal = period === 'last30' ? st.last30Total : period === 'lastMonth' ? st.lastTotal : st.curTotal;

  // Group filtered receipts into This week / Last week / Earlier (weeks start Mon).
  const today = localToday();
  const monOf = (iso) => { const d = new Date(iso + 'T00:00:00Z'); const dow = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - dow); return d.toISOString().slice(0, 10); };
  const addD = (iso, n) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
  const thisMon = monOf(today), lastMon = addD(thisMon, -7);
  const filtered = supplier === 'all' ? st.rows : st.rows.filter((r) => r.vendorId === supplier);
  const groups = [{ key: 'this', label: t('receipts.thisWeek', 'This week'), rows: [] }, { key: 'last', label: t('receipts.lastWeek', 'Last week'), rows: [] }, { key: 'earlier', label: t('receipts.earlier', 'Earlier'), rows: [] }];
  for (const r of filtered) { const dt = r._d; if (dt >= thisMon) groups[0].rows.push(r); else if (dt >= lastMon) groups[1].rows.push(r); else groups[2].rows.push(r); }
  const groupTotal = (g) => g.rows.reduce((s, r) => s + (r.totalCents || 0), 0);

  return (
    <KShell dark={dark} tab="receipts" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back={false} big title={t('header.receipts', 'Receipts')} subtitle={t('sub.receipts', 'Capture, review, file')} right={<PersonaChip persona={persona}/>}/>

        {st.loading && <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>Loading…</div>}

        {/* Vendor spend card — period tabs + big headline + (+) add + Bills due / CC spent slider + vendor rows */}
        {!st.loading && (st.vendors.length > 0 || st.billsDueCents > 0 || st.ccSpendCents > 0) && (
          <div style={{ padding: '0 20px 14px' }}>
            <div className="card" style={{ padding: 18 }}>
              {/* Header row — links through to Bills due / CC spent, same target as the slider below */}
              <KLink to={sideTo} asButton>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div className="label">{t('receipts.vendorSpend', 'Vendor spend')}</div>
                  <Chev/>
                </div>
              </KLink>

              {/* Period tabs — pick the window the headline total below reflects */}
              <div role="tablist" aria-label={t('receipts.period', 'Period')} style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface-3,#f0f0f3)', borderRadius: 10, marginBottom: 10 }}>
                {[['month', t('receipts.thisMonth', 'This month')], ['last30', t('receipts.last30Days', 'Last 30 days')], ['lastMonth', t('receipts.lastMonth', 'Last month')]].map(([k, lbl]) => {
                  const on = period === k;
                  return (
                    <button key={k} role="tab" aria-selected={on} onClick={() => setPeriod(k)}
                      style={{ flex: 1, padding: '7px 6px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        background: on ? 'var(--surface,#fff)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)',
                        boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{lbl}</button>
                  );
                })}
              </div>

              {/* Headline total + (+) add a receipt */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <KMoney cents={periodTotal} currency={st.currency} locale={st.locale} size="xl" tone="danger"/>
                <button onClick={() => setAddOpen(true)} aria-label={t('receipts.addCta', 'Add a receipt')}
                  style={{ width: 40, height: 40, borderRadius: 20, flexShrink: 0, border: 'none', cursor: 'pointer',
                    background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Ico.plus}
                </button>
              </div>

              {/* Segmented slider for secondary metric */}
              <div role="tablist" aria-label="Secondary metric" style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface-3,#f0f0f3)', borderRadius: 10, marginTop: 12 }}>
                {[['bills', t('receipts.billsDue', 'Bills due')], ['cc', t('receipts.ccSpent', 'Credit card spent')]].map(([k, lbl]) => {
                  const on = sideTab === k;
                  return (
                    <button key={k} role="tab" aria-selected={on} onClick={() => setSideTab(k)}
                      style={{ flex: 1, padding: '7px 8px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        background: on ? 'var(--surface,#fff)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)',
                        boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{lbl}</button>
                  );
                })}
              </div>
              {/* Balances read to the cent (fmtMoneyExact) — a "$1k" compact
                  outstanding is useless for deciding what check to write. */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--ink-2)' }}>{sideTab === 'bills' ? t('receipts.outstanding', 'Outstanding') : `${t('receipts.ccSpent', 'Credit card spent')} · ${ymLabel(st.m0)}`}</span>
                <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: sideTab === 'bills' && sideValue > 0 ? 'var(--warning)' : 'var(--ink)' }}>{fmtMoneyExact(sideValue, st.currency, st.locale)}</span>
              </div>
              {/* What the LAST closed statement period cost — the reference point
                  that makes the outstanding number above it readable. Tap to
                  drill into the invoices behind it (reconcile vs the vendor's
                  statement). */}
              {sideTab === 'bills' && st.lastPeriodPaid && (() => {
                const paidIso = st.lastPeriodPaid.vendors[0]?.paidIso || null;
                const hasExcluded = st.lastPeriodPaid.vendors.some((v) => v.excluded.length > 0);
                return (
                  <button onClick={() => setPaidOpen(true)} aria-label={t('receipts.prevPeriodPaid', 'Previous period paid')}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      marginTop: 6, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>
                    <span style={{ color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {t('receipts.prevPeriodPaid', 'Previous period paid')}
                      {paidIso ? ` · ${new Date(paidIso + 'T00:00:00Z').toLocaleDateString(st.locale, { month: 'short', day: 'numeric', timeZone: 'UTC' })}` : ''}
                      {hasExcluded && <span title={t('receipts.someExcluded', 'Some invoices not counted')}
                        style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--warning)', flexShrink: 0 }}/>}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{fmtMoneyExact(st.lastPeriodPaid.totalCents, st.currency, st.locale)}</span>
                      <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}><Chev/></span>
                    </span>
                  </button>
                );
              })()}

              {/* Vendor rows — expandable trend; chev signals it opens. */}
              {st.vendors.length > 0 && (
                <div style={{ marginTop: 14, paddingTop: 8, borderTop: '1px solid var(--hairline)' }}>
                  {st.vendors.map((v, i) => {
                    const open = sel === v.vendorId;
                    return (
                      <div key={v.vendorId} style={{ borderBottom: i < st.vendors.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
                        <button onClick={() => setSel(open ? null : v.vendorId)} aria-expanded={open}
                          style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 0', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vendorName(v.name)}</div>
                            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{ymLabel(st.m1)}: {M(v.last)}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <div className="mono" style={{ fontSize: 14.5, fontWeight: 600 }}>{M(v.cur)}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: v.isNew ? 'var(--ink-3)' : v.pct > 0 ? 'var(--danger,#d2563f)' : v.pct < 0 ? 'var(--positive,#2e9e5b)' : 'var(--ink-3)' }}>
                                {v.isNew ? 'new' : v.pct == null ? '—' : `${v.pct > 0 ? '▲' : '▼'} ${Math.abs(v.pct).toFixed(Math.abs(v.pct) < 10 ? 1 : 0)}%`}
                              </div>
                            </div>
                            <span style={{ display: 'inline-flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><Chev/></span>
                          </div>
                        </button>
                        {open && <div style={{ padding: '0 0 14px' }}><VendorMonthsChart months={v.months} M={M} /></div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Receipts — weekly groups with per-period totals, filterable by supplier */}
        {!st.loading && st.rows.length > 0 && (
          <div style={{ padding: '8px 20px 0' }}>
            {st.vendorList.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <select value={supplier} onChange={(e) => setSupplier(e.target.value)} aria-label="Filter receipts by supplier"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}>
                  <option value="all">All suppliers</option>
                  {st.vendorList.map(([vid, name]) => <option key={vid} value={vid}>{name}</option>)}
                </select>
              </div>
            )}
            {groups.map((g) => (g.rows.length > 0 || g.key === 'this') && (
              <div key={g.key} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: g.rows.length ? '1px solid var(--rule)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{g.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{g.rows.length} receipt{g.rows.length === 1 ? '' : 's'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="mono" style={{ fontSize: 15, fontWeight: 700 }}>{M(groupTotal(g))}</span>
                    {g.key === 'this' && (
                      <KLink to="/receipts/recent" asButton>
                        <span aria-label="All recent receipts" style={{ fontSize: 20, lineHeight: 1, color: 'var(--ink-3)', padding: '0 2px' }}>⋯</span>
                      </KLink>
                    )}
                  </div>
                </div>
                {g.rows.length === 0 && g.key === 'this' && (
                  <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--ink-3)' }}>No receipts this week yet.</div>
                )}
                {g.rows.slice(0, 6).map((r, i, arr) => (
                  <ReceiptRowItem key={r.invoiceId} r={r} M={M} last={i === Math.min(arr.length, 6) - 1} />
                ))}
                {g.rows.length > 6 && (
                  <KLink to="/receipts/recent" asButton>
                    <div style={{ padding: '11px 16px', fontSize: 13, fontWeight: 600, color: 'var(--brand)' }}>View all {g.rows.length} →</div>
                  </KLink>
                )}
              </div>
            ))}
          </div>
        )}

        {!st.loading && st.rows.length === 0 && st.vendors.length === 0 && (
          <div style={{ padding: '0 20px' }}>
            <Empty title="No receipts yet" body="Scan an invoice or receipt and it'll appear here, grouped by week and supplier." />
            <button onClick={() => setAddOpen(true)} className="btn primary lg" style={{ width: '100%', cursor: 'pointer', marginTop: -8 }}>
              {t('receipts.addCta', 'Add a receipt')}
            </button>
          </div>
        )}
      </div>

      <PaidPeriodSheet open={paidOpen} onClose={() => setPaidOpen(false)}
        lp={st.lastPeriodPaid} currency={st.currency} locale={st.locale} />
      <AddReceiptSheet open={addOpen} onClose={() => setAddOpen(false)} />
    </KShell>
  );
}

// ─── (+) Add a receipt — scan / upload / enter manually ─────────────────────
function AddReceiptSheet({ open, onClose }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const go = (to) => { onClose(); nav.navigate(to); };
  const options = [
    // Scan and Upload are the same screen now (camera + upload side by side),
    // so the sheet offers one entry instead of two rows that landed identically.
    { key: 'scan', to: '/receipts/scan', icon: Ico.scan, label: t('receipts.add.scan', 'Scan'), hint: t('receipts.add.scanHint', 'Use your camera or upload a file') },
    { key: 'manual', to: '/receipts/manual', icon: Ico.keyboard, label: t('receipts.add.manual', 'Enter manually'), hint: t('receipts.add.manualHint', 'No receipt to scan') },
  ];
  return (
    <Sheet open={open} onClose={onClose} title={t('receipts.add.title', 'Add a receipt')}>
      <div style={{ padding: '4px 16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((o) => (
          <button key={o.key} onClick={() => go(o.to)}
            style={{ width: '100%', padding: '14px 16px', border: 'none', background: 'var(--surface-2,var(--surface-3))', borderRadius: 14,
              display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{o.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{o.label}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1 }}>{o.hint}</div>
            </div>
            <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}><Chev/></span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

// ─── Previous-period-paid drill-in ───────────────────────────────────────────
// The invoices behind the receipts card's "Previous period paid" line, per
// deferred-payment vendor: what was counted (compare line-by-line against the
// vendor's statement) and what was NOT (duplicate / manually-unpaid), so a
// mismatch against the statement total is explained rather than mysterious.
function paidPeriodRangeLabel(closeIso, method, locale) {
  const closeDay = Number(closeIso.slice(8, 10));
  const startDay = method === 'monthly' ? 1 : closeDay === 15 ? 1 : 16;
  const d0 = new Date(`${closeIso.slice(0, 8)}${String(startDay).padStart(2, '0')}T00:00:00Z`);
  const d1 = new Date(closeIso + 'T00:00:00Z');
  const f = (d, opts) => d.toLocaleDateString(locale, { ...opts, timeZone: 'UTC' });
  return `${f(d0, { month: 'short', day: 'numeric' })}–${f(d1, { day: 'numeric' })}`;
}

function PaidPeriodSheet({ open, onClose, lp, currency, locale }) {
  useT();
  if (!open || !lp) return null;
  const $ = (c) => fmtMoneyExact(c, currency, locale);
  const dayLabel = (iso) => (iso ? new Date(iso + 'T00:00:00Z').toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');
  const reasonLabel = (r) => (r === 'duplicate' ? t('receipts.reason.duplicate', 'Duplicate')
    : r === 'unpaid' ? t('receipts.reason.unpaid', 'Marked unpaid') : r);
  return (
    <Sheet open={open} onClose={onClose} title={t('receipts.prevPeriodPaid', 'Previous period paid')}>
      <div style={{ overflowY: 'auto', padding: '4px 16px 20px' }}>
        {lp.vendors.map((v) => (
          <div key={v.vendorId} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.vendorName}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>
                  {paidPeriodRangeLabel(v.closeIso, v.method, locale)} · {t('receipts.paidOn', 'paid')} {dayLabel(v.paidIso)}
                </div>
              </div>
              <span className="mono" style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{$(v.totalCents)}</span>
            </div>

            {/* Counted invoices — line up against the vendor's statement. */}
            <div style={{ marginTop: 10 }}>
              {v.invoices.map((inv) => (
                <div key={inv.invoiceId} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '7px 0', borderTop: '1px solid var(--hairline)' }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-2)', minWidth: 0 }}>
                    {inv.invoiceNumber ? `#${inv.invoiceNumber}` : t('receipts.noNumber', 'no number')}
                    <span style={{ color: 'var(--ink-3)' }}> · {dayLabel(inv.issuedDate)}</span>
                  </span>
                  <span className="mono" style={{ fontSize: 13.5, fontWeight: 600 }}>{$(inv.totalCents)}</span>
                </div>
              ))}
            </div>

            {/* Not counted — the usual cause of a mismatch vs the statement. */}
            {v.excluded.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--rule)' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--warning)' }}>
                  {t('receipts.notCounted', 'Not counted')} · {$(v.excludedCents)}
                </div>
                {v.excluded.map((inv) => (
                  <div key={inv.invoiceId} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '7px 0' }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-3)', minWidth: 0 }}>
                      {inv.invoiceNumber ? `#${inv.invoiceNumber}` : t('receipts.noNumber', 'no number')}
                      <span> · {dayLabel(inv.issuedDate)}</span>
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 5, background: 'var(--surface-3,#f0f0f3)', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>{reasonLabel(inv.reason)}</span>
                    </span>
                    <span className="mono" style={{ fontSize: 13.5, color: 'var(--ink-3)', textDecoration: 'line-through' }}>{$(inv.totalCents)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 4 }}>
          {t('receipts.reconcileNote', 'Compare against the vendor’s statement. Only invoices dated in this period show here — a missing one is likely unscanned or dated to another period.')}
        </div>
      </div>
    </Sheet>
  );
}

// ─── Buy menu ───────────────────────────────────────────────
// Local copies (cross-file top-level symbols aren't shared unless put on window;
// inlining keeps the Buy tab self-contained and immune to load-order/cache issues).
function buyVendorInitial(name) { const t = (name || '?').trim(); return t[0]?.toUpperCase() || '?'; }
const BUY_PAYMENT_LABEL = {
  card: 'Credit card', check_on_delivery: 'Check on delivery',
  statement: 'Statement (semi-monthly)', monthly: 'Monthly statement', terms: 'On account / net terms',
};

// Buy tab = the vendors list. Tap a vendor for details/terms; tap the order icon
// to jump straight into the shopping-list order form pre-selected to that vendor.
function BuyMenu({ dark = false, persona = 'owner' }) {
  useT();
  const [st, setSt] = React.useState({ loading: true, vendors: [] });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, vendors: [] }); return; }
        const vendors = await window.kanjo.procurement.vendors.listActive();
        vendors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setSt({ loading: false, vendors });
      } catch (e) { console.warn('[BuyMenu] load failed', e); if (!cancelled) setSt({ loading: false, vendors: [] }); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back={false} big title={t('header.buy', 'Buy')} subtitle={t('sub.buy', 'Pick a supplier to order from')} right={<PersonaChip persona={persona}/>}/>
        <div style={{ padding: '0 16px' }}>
          {/* Price check — the in-store "what did we pay last?" lookup. Pinned at
              the top of Buy since checking a price standing in a store is a
              primary use; taps through to the search-first PriceCheck screen. */}
          <KLink to="/buy/price-check" asButton style={{ display: 'block', margin: '2px 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px',
              borderRadius: 12, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--hairline)' }}>
              <span style={{ color: 'var(--ink-2)', display: 'flex', flexShrink: 0 }}>{Ico.search}</span>
              <span style={{ fontSize: 14.5, color: 'var(--ink-2)' }}>{t('buy.priceCheck', 'Check a price — what did we pay last?')}</span>
            </div>
          </KLink>
          {/* Compare a prospective supplier's product list against what we buy:
              upload a PDF/photo → OCR + Claude find the gaps and price deltas. */}
          <KLink to="/buy/new-vendor" asButton style={{ display: 'block', margin: '2px 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px',
              borderRadius: 12, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--hairline)' }}>
              <span style={{ color: 'var(--ink-2)', display: 'flex', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 3h9l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/>
                  <path d="M13 3v5h5M8 13l2 2 4-4"/>
                </svg>
              </span>
              <span style={{ fontSize: 14.5, color: 'var(--ink-2)' }}>{t('buy.newVendor', "Compare a new vendor's list")}</span>
            </div>
          </KLink>
          {st.loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>Loading…</div>}
          {!st.loading && st.vendors.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>No vendors yet. Scan a receipt to add one.</div>
          )}
          {st.vendors.map((v) => (
            <div key={v.vendorId} style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--hairline)' }}>
              {/* Order cart — left affordance, opens the shopping list with this supplier pre-selected. */}
              <KLink to={`/buy/list?vendor=${encodeURIComponent(v.vendorId)}`} asButton>
                <div role="img" aria-label={`Order from ${vendorName(v.name)}`} title="Build an order"
                  style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, marginLeft: 2, background: 'var(--positive-soft)', color: 'var(--positive)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Ico.cart}
                </div>
              </KLink>
              {/* Vendor name / payment label — taps through to vendor details; chev is the drill-in indicator. */}
              <KLink to={`/buy/vendor/detail?id=${encodeURIComponent(v.vendorId)}`} asButton style={{ flex: 1, minWidth: 0, display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '13px 4px 13px 0', minWidth: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vendorName(v.name)}</div>
                    <div style={{ fontSize: 12.5, color: v.paymentMethod ? 'var(--on-dim)' : 'var(--warning)' }}>
                      {v.paymentMethod ? (BUY_PAYMENT_LABEL[v.paymentMethod] || v.paymentMethod) : 'Set payment terms →'}
                    </div>
                  </div>
                  <Chev/>
                </div>
              </KLink>
            </div>
          ))}
        </div>
      </div>
    </KShell>
  );
}

// ─── Reports menu ───────────────────────────────────────────

// One async pass computing the tile status indicators (lib/reports/tileSignals).
// null while loading; {} when signed out or the pass failed — tiles then fall
// back to their muted "awaiting data" states.
function useReportSignals() {
  const [sig, setSig] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    // A stale cached kanjo-lib.js may not export reportSignals yet — an
    // unguarded `.then` on undefined here throws INSIDE the effect and blanks
    // the whole app. Everything is fail-soft to {}.
    let p = null;
    try { p = window.kanjo?.reportSignals?.computeReportSignals?.(); } catch (e) { console.warn('[Reports] signals threw', e); }
    if (!p || typeof p.then !== 'function') { setSig({}); return undefined; }
    p.then((r) => { if (!cancelled) setSig(r || {}); })
      .catch((e) => { console.warn('[Reports] signals failed', e); if (!cancelled) setSig({}); });
    return () => { cancelled = true; };
  }, []);
  return sig;
}

// Compact ±percent for indicator labels: "▲ 4.1%" / "▼ 12%".
const tilePct = (pct) => `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`;
const ymShort = (ym, locale) => {
  const [y, m] = (ym || '').split('-').map(Number);
  return y ? new Date(Date.UTC(y, (m || 1) - 1, 1)).toLocaleDateString(locale || 'en-US', { month: 'short', timeZone: 'UTC' }) : '';
};

function ReportsMenu({ dark = false, persona = 'owner' }) {
  useT();
  // Each report has its own period control; the Reports menu doesn't gate them.
  // Tile subtitles use a fixed 'month' window for at-a-glance numbers.
  const c = useMenuCounts('month');
  const sig = useReportSignals();

  // Show the admin dashboard tile only to members of the `admins` group.
  const [isAdmin, setIsAdmin] = React.useState(false);
  React.useEffect(() => {
    window.kanjo?.auth?.currentClaims?.().then((cl) => {
      let g = cl && cl['cognito:groups'];
      if (typeof g === 'string') g = g.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
      setIsAdmin(Array.isArray(g) && g.includes('admins'));
    }).catch(() => {});
  }, []);

  // ── Tile indicators — each an array of {tone, label}; null = skeleton. ──
  const awaiting = (label) => [{ tone: 'muted', label }];
  // Indicator math must NEVER crash the menu render — a thrown label
  // formatter would unmount the whole app. Bad math degrades to a muted dash.
  const safeInd = (fn) => {
    try { return fn(); } catch (e) { console.warn('[Reports] tile indicator failed', e); return awaiting('—'); }
  };

  // Sales: last 4 complete Mon–Sun weeks vs the prior 4 (settlement-fair —
  // the in-progress week never counts, so a Sunday doesn't read as a drop).
  const salesInd = sig == null ? null : safeInd(() => sig.sales
    ? [{
        tone: sig.sales.tone === 'up' ? 'green' : sig.sales.tone === 'down' ? 'red' : 'muted',
        label: sig.sales.tone === 'steady'
          ? t('reports.tile.salesSteady', 'Steady · last 4 wks')
          : `${tilePct(sig.sales.pct)} · ${t('reports.tile.salesWindow', '4 wks vs prior')}`,
      }]
    : awaiting(t('reports.tile.salesAwait', 'Building deposit history')));

  // Food cost: the gauge status straight from cogs.calc (target + amber band).
  const foodInd = c.loading ? null : safeInd(() => c.foodCostPct14d != null && c.foodCostStatus && c.foodCostStatus !== 'no-sales'
    ? [{ tone: c.foodCostStatus, label: `${c.foodCostPct14d.toFixed(1)}% · ${t('reports.foodCostAvgSuffix', '14d avg')}` }]
    : c.cogs14dCents > 0
      ? [{ tone: 'muted', label: `${fmtMoneyM(c.cogs14dCents, c.currency, c.locale)} ${t('reports.foodCostSpendSuffix', 'spend · 14d')}` }]
      : awaiting(t('reports.foodCostAwait', 'Awaiting data')));

  // Cash flow: 13-week forecast health.
  const cashInd = sig == null ? null : safeInd(() => sig.cash
    ? [sig.cash.tone === 'green'
        ? { tone: 'green', label: t('reports.tile.cashOk', 'No gap · 13-wk forecast') }
        : { tone: sig.cash.tone, label: t('reports.tile.cashGap', 'Gap in week {n}').replace('{n}', String(sig.cash.gapWeek)) }]
    : awaiting(t('reports.tile.cashAwait', 'Connect a bank')));

  // Price changes: amber when any item moved >10% since its prior purchase.
  const priceInd = c.loading ? null : safeInd(() => {
    const inds = [];
    if (c.priceMoversCount > 0) inds.push({ tone: c.priceMoversMaxPct > 10 ? 'amber' : 'muted',
      label: `${c.priceMoversCount} ${t('reports.itemsMovedSuffix', 'moved')}${c.priceMoversMaxPct > 10 ? ` · ${t('reports.tile.priceTop', 'top')} ${tilePct(c.priceMoversMaxPct)}` : ''}` });
    // Suspect (>±50%) moves held for review — surfaces the same "needs a unit
    // check" cue the Today widget shows, so it isn't missed on the Reports grid.
    if (c.priceReviewCount > 0) inds.push({ tone: 'amber', label: `${c.priceReviewCount} ${t('reports.tile.toReview', 'to review')}` });
    return inds.length ? inds : awaiting(t('reports.noMoves', 'No moves'));
  });

  // Payroll: countdown to PREP day — the Friday the checks get written
  // (green ≥3d, amber <3d, red tomorrow/today). Once prep day has passed the
  // checks are on their way, so it flips to a quiet "checks land in N days".
  const payrollInd = sig == null ? null : safeInd(() => {
    const p = sig.payroll;
    if (!p) return awaiting(t('reports.tile.payrollAwait', 'Awaiting check history'));
    if (p.overdue) return [{ tone: 'amber', label: t('reports.tile.payrollGap', 'No checks in {d} days').replace('{d}', String(p.daysSince)) }];
    if (p.phase === 'landing') {
      const label = p.daysUntil === 0 ? t('reports.tile.payrollLandToday', 'Payroll checks landing')
        : t('reports.tile.payrollLandIn', 'Checks land in {d} days').replace('{d}', String(p.daysUntil));
      return [{ tone: 'green', label }];
    }
    const label = p.daysUntil === 0 ? t('reports.tile.payrollPrepToday', 'Write payroll checks today')
      : p.daysUntil === 1 ? t('reports.tile.payrollPrepTomorrow', 'Write payroll checks tomorrow')
      : t('reports.tile.payrollPrepIn', 'Payroll prep in {d} days').replace('{d}', String(p.daysUntil));
    return [{ tone: p.tone, label }];
  });

  // Supplies: last complete month vs the trailing 3-month average.
  const suppliesInd = sig == null ? null : safeInd(() => !sig.supplies?.hasFeed
    ? awaiting(t('reports.tile.suppliesNoCard', 'Connect a card'))
    : sig.supplies.signal
      ? [{ tone: sig.supplies.signal.tone,
           label: Math.abs(sig.supplies.signal.pct) <= 3
             ? t('reports.tile.suppliesSteady', 'Steady vs 3-mo avg')
             : `${tilePct(sig.supplies.signal.pct)} ${t('reports.tile.suppliesVs', 'vs 3-mo avg')}` }]
      : awaiting(t('reports.tile.suppliesAwait', 'Awaiting data')));

  // Rent & utilities: increases only (owner request 2026-07-12) — the annual
  // rent step-up, and the utility bills' month-over-month swing.
  const rentInd = sig == null ? null : safeInd(() => [
    sig.rent?.increase?.kind === 'increase'
      ? { tone: 'red', label: `${t('reports.tile.rentUp', 'Rent')} ${tilePct(sig.rent.increase.pct)} · ${ymShort(sig.rent.increase.ym, c.locale)}` }
      : sig.rent?.increase?.kind === 'none'
        ? { tone: 'green', label: t('reports.tile.rentFlat', 'Rent unchanged · 12 mo') }
        : { tone: 'muted', label: t('reports.tile.rentAwait', 'Awaiting rent history') },
    ...(sig.rent?.utilities
      ? [{ tone: sig.rent.utilities.tone === 'up' ? 'amber' : sig.rent.utilities.tone === 'down' ? 'green' : 'muted',
           label: sig.rent.utilities.tone === 'steady'
             ? t('reports.tile.utilSteady', 'Utilities steady MoM')
             : `${t('reports.tile.util', 'Utilities')} ${tilePct(sig.rent.utilities.pct)} ${t('reports.tile.mom', 'MoM')}` }]
      : []),
  ]);

  // Insurance: the rate-increase detection the report already runs.
  const insInd = sig == null ? null : safeInd(() => sig.insurance?.increase
    ? [{ tone: 'amber', label: `${tilePct(sig.insurance.increase.pctIncrease)} ${t('reports.tile.insUp', 'YoY increase')}` }]
    : sig.insurance?.hasPayments
      ? [{ tone: 'green', label: t('reports.tile.insSteady', 'Premiums steady') }]
      : awaiting(t('reports.tile.insAwait', 'No premiums detected')));

  // Capex: neutral YTD vs last year (spend isn't good/bad), plus a nudge when
  // something was tagged this month.
  const capexInd = (sig == null || c.loading) ? null : safeInd(() => sig.capex && (sig.capex.ytdCents > 0 || sig.capex.lastYearCents > 0)
    ? [
        { tone: 'muted', label: `${t('reports.tile.capexYtd', 'YTD')} ${fmtMoneyM(sig.capex.ytdCents, c.currency, c.locale)} · ${sig.capex.priorYear} ${fmtMoneyM(sig.capex.lastYearCents, c.currency, c.locale)}` },
        ...(sig.capex.newThisMonth ? [{ tone: 'amber', label: t('reports.tile.capexNew', 'New purchase this month') }] : []),
      ]
    : awaiting(t('reports.tile.capexNone', 'None tagged yet')));

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back={false} big title={t('header.reports', 'Reports')} subtitle={t('sub.reports', 'How the business is doing')} right={<PersonaChip persona={persona}/>}/>

        <MenuGrid>
          {/* "Sales close" (manual per-day gross entry) is hidden: revenue comes
              from bank deposits, not day-to-day POS numbers. "Sales trends"
              (deposit-based) is the sales view. Re-enable the tile if a POS
              daily-sales feed is added. */}
          <MenuTile to="/reports/sales-trends"
            icon={Ico.trendUp}   iconColor="var(--positive)" iconBg="var(--positive-soft)"
            title={t('reports.sales', 'Sales')}            indicators={salesInd}/>
          <MenuTile to="/reports/cogs"
            icon={Ico.gauge}     iconColor="var(--warning)"  iconBg="var(--warning-soft)"
            title={t('reports.foodCost', 'Food cost')}        indicators={foodInd}/>
          <MenuTile to="/reports/cash"
            icon={Ico.wave}      iconColor="var(--positive)" iconBg="var(--positive-soft)"
            title={t('reports.cashFlow', 'Cash flow')}        indicators={cashInd}/>
          <MenuTile to="/reports/price-changes"
            icon={Ico.tag}       iconColor="var(--warning)"  iconBg="var(--warning-soft)"
            title={t('reports.priceChanges', 'Price changes')}    indicators={priceInd}/>
          <MenuTile to="/reports/taxes"
            icon={Ico.receipt || Ico.tag}  iconColor="var(--danger)"   iconBg="var(--warning-soft)"
            title={t('reports.taxes', 'Taxes')}              subtitle={t('reports.taxesTileSub', 'Excise & payroll · auto-detected')}/>
          <MenuTile to="/reports/payroll"
            icon={Ico.wave}      iconColor="var(--brand)"    iconBg="var(--brand-soft)"
            title={t('reports.payroll', 'Payroll')}          indicators={payrollInd}/>
          <MenuTile to="/reports/supplies"
            icon={Ico.receipt || Ico.tag}  iconColor="var(--danger)"   iconBg="var(--warning-soft)"
            title={t('reports.supplies', 'Supplies')}        indicators={suppliesInd}/>
          <MenuTile to="/reports/rent"
            icon={Ico.receipt || Ico.tag}  iconColor="var(--ink-2)"    iconBg="var(--surface-3)"
            title={t('reports.rent', 'Rent, utilities & maintenance')}    indicators={rentInd}/>
          <MenuTile to="/reports/insurance"
            icon={Ico.receipt || Ico.tag}  iconColor="var(--danger)"   iconBg="var(--danger-soft)"
            title={t('reports.insurance', 'Insurance')}      indicators={insInd}/>
          <MenuTile to="/reports/capex"
            icon={Ico.gauge}     iconColor="var(--brand)"    iconBg="var(--brand-soft)"
            title={t('reports.capex', 'Capital expenses')}   indicators={capexInd}/>
          <MenuTile to="/reports/census"
            icon={Ico.gauge}     iconColor="var(--ink-2)"    iconBg="var(--surface-3)"
            title={t('reports.census', 'Census Bureau Data')} subtitle={t('reports.censusTileSub', 'County economic context')}/>
          {isAdmin && (
            <MenuTile to="/admin"
              icon={Ico.gauge}   iconColor="var(--ink-2)"    iconBg="var(--surface-3)"
              title={t('reports.adminDash', 'Admin dashboard')}  subtitle={t('reports.adminSub', 'System health & metrics')}/>
          )}
        </MenuGrid>
      </div>
    </KShell>
  );
}

// ─── Recent receipts — lists scanned invoices from the local DB ─────────────
function RecentReceipts({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true, rows: [], currency: 'USD', locale: 'en-US' });
  const [vendorFilter, setVendorFilter] = React.useState('all');
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, rows: [] }); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        // Last 90 days by INVOICE date (fall back to upload date if undated),
        // most-recent first.
        const today = localToday();
        const cutoff = (() => { const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 90); return d.toISOString().slice(0, 10); })();
        const dateOf = (i) => i.issuedDate || (i.createdAt ? new Date(i.createdAt).toISOString().slice(0, 10) : '');
        const invoices = (await db.invoices.where('userId').equals(userId).toArray())
          .filter((i) => dateOf(i) >= cutoff);
        invoices.sort((a, b) => {
          const da = dateOf(a), dbb = dateOf(b);
          if (da !== dbb) return da < dbb ? 1 : -1;        // most recent invoice date first
          return (b.createdAt || 0) - (a.createdAt || 0);  // tie-break: newest upload first
        });
        const vmap = {};
        for (const vid of [...new Set(invoices.map((i) => i.vendorId).filter(Boolean))]) {
          const v = await db.vendors.get(vid); if (v) vmap[vid] = vendorName(v.name);
        }
        const rows = [];
        for (const i of invoices) {
          const lineCount = await db.lineItems.where('invoiceId').equals(i.invoiceId).count().catch(() => 0);
          rows.push({ ...i, vendorName: vmap[i.vendorId] || 'Unknown vendor', lineCount });
        }
        const profile = await db.userProfile.get(userId).catch(() => null);
        if (!cancelled) setSt({ loading: false, rows, currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) {
        if (!cancelled) setSt({ loading: false, rows: [], error: String(e?.message || e) });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <KShell dark={dark} tab="receipts" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title="Recent receipts" subtitle="Last 90 days · grouped by week" right={<PersonaChip persona={persona}/>}/>
        <div style={{ padding: '0 16px' }}>
          {st.loading && <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>Loading…</div>}
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {!st.loading && !st.error && st.rows.length === 0 && (
            <Empty title="No receipts yet" body="Scan an invoice or receipt and it'll appear here." />
          )}
          {(() => {
            // Supplier filter — shown only when there's more than one supplier.
            const vendors = Array.from(new Map(st.rows.map((r) => [r.vendorId, r.vendorName])).entries())
              .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
            const visible = vendorFilter === 'all' ? st.rows : st.rows.filter((r) => r.vendorId === vendorFilter);
            return (
              <>
                {vendors.length > 1 && (
                  <div style={{ padding: '4px 0 12px' }}>
                    <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}
                            aria-label="Filter by supplier"
                            style={{ width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 10,
                                     border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}>
                      <option value="all">All suppliers ({st.rows.length})</option>
                      {vendors.map(([vid, name]) => (
                        <option key={vid} value={vid}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {!st.loading && visible.length === 0 && st.rows.length > 0 && (
                  <div style={{ color: 'var(--on-dim)', fontSize: 13, padding: 16, textAlign: 'center' }}>No receipts for this supplier in the last 90 days.</div>
                )}
                {(() => {
                  // Group (newest-first) into This week / Last week / Earlier.
                  // Weeks start Monday; "Earlier" is everything older than last
                  // week within the 90-day window.
                  const today = localToday();
                  const dOf = (r) => r.issuedDate || (r.createdAt ? new Date(r.createdAt).toISOString().slice(0, 10) : '');
                  const addDaysIso = (iso, n) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
                  const mondayOf = (iso) => { const d = new Date(iso + 'T00:00:00Z'); const dow = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - dow); return d.toISOString().slice(0, 10); };
                  const thisMon = mondayOf(today);
                  const lastMon = addDaysIso(thisMon, -7);
                  const groups = [
                    { key: 'this', label: 'This week', rows: [] },
                    { key: 'last', label: 'Last week', rows: [] },
                    { key: 'earlier', label: 'Earlier', rows: [] },
                  ];
                  for (const r of visible) {
                    const dt = dOf(r);
                    if (dt >= thisMon) groups[0].rows.push(r);
                    else if (dt >= lastMon) groups[1].rows.push(r);
                    else groups[2].rows.push(r);
                  }
                  const ReceiptRow = (r, i, arr) => (
                    <KLink key={r.invoiceId} to={`/receipts/detail?id=${encodeURIComponent(r.invoiceId)}`} asButton style={{ display: 'block' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
                        <div style={{ minWidth: 0, paddingRight: 12 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.vendorName}</div>
                          <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                            {r.issuedDate || '—'}{r.invoiceNumber ? ` · #${r.invoiceNumber}` : ''} · {r.lineCount} item{r.lineCount === 1 ? '' : 's'}{r.isDuplicate ? ' · ⚠ duplicate' : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtMoneyExact(r.totalCents || 0, st.currency, st.locale)}</div>
                      </div>
                    </KLink>
                  );
                  return groups.filter((g) => g.rows.length).map((g) => (
                    <div key={g.key} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '18px 4px 8px' }}>
                        {g.label} <span style={{ fontWeight: 500 }}>· {g.rows.length}</span>
                      </div>
                      <div className="card" style={{ padding: '2px 16px' }}>
                        {g.rows.map(ReceiptRow)}
                      </div>
                    </div>
                  ));
                })()}
              </>
            );
          })()}
        </div>
      </div>
    </KShell>
  );
}

// ─── Invoice detail — full line items + totals for one scanned receipt ───────
function InvoiceDetail({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);
  const id = useHashQuery('id');
  const [st, setSt] = React.useState({ loading: true });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  const [editingLineId, setEditingLineId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [menuLineId, setMenuLineId] = React.useState(null);
  const [photoOpen, setPhotoOpen] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(null);
  const [imageMissing, setImageMissing] = React.useState(false);

  const onDiscardDuplicate = async () => {
    if (busy) return;
    setBusy(true);
    try { await window.kanjo.ocr.deleteInvoice(id); nav.navigate('/receipts'); }
    catch (e) { console.warn('[InvoiceDetail] discard failed', e); setBusy(false); }
  };
  const onKeepDuplicate = async () => {
    if (busy) return;
    setBusy(true);
    try { await window.kanjo.ocr.unmarkDuplicate(id); reload(); }
    catch (e) { console.warn('[InvoiceDetail] keep failed', e); }
    finally { setBusy(false); }
  };
  const onSaveLine = async (lineItemId, patch) => {
    setBusy(true);
    try { await window.kanjo.ocr.updateLineItem(lineItemId, patch); setEditingLineId(null); reload(); }
    catch (e) { console.warn('[InvoiceDetail] line edit failed', e); }
    finally { setBusy(false); }
  };
  const onSetPaid = async (state) => {
    setBusy(true);
    try { await window.kanjo.cashflow.payments.setInvoicePaid(id, state); reload(); }
    catch (e) { console.warn('[InvoiceDetail] set paid failed', e); }
    finally { setBusy(false); }
  };
  const [editingDate, setEditingDate] = React.useState(false);
  const onSaveDate = async (next) => {
    setBusy(true);
    try { await window.kanjo.ocr.updateInvoiceDate(id, next); setEditingDate(false); reload(); }
    catch (e) { console.warn('[InvoiceDetail] date edit failed', e); }
    finally { setBusy(false); }
  };
  const [reassignOpen, setReassignOpen] = React.useState(false);
  const onReassign = async (target) => {
    setBusy(true);
    try { await window.kanjo.ocr.reassignInvoice(id, target); setReassignOpen(false); reload(); }
    catch (e) { console.warn('[InvoiceDetail] reassign failed', e); }
    finally { setBusy(false); }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo || !id) { if (!cancelled) setSt({ loading: false, notFound: true }); return; }
      try {
        const db = window.kanjo.db;
        const inv = await db.invoices.get(id);
        if (!inv) { if (!cancelled) setSt({ loading: false, notFound: true }); return; }
        const lines = await db.lineItems.where('invoiceId').equals(id).toArray();
        const vendor = inv.vendorId ? await db.vendors.get(inv.vendorId) : null;
        const profile = await db.userProfile.get(inv.userId).catch(() => null);

        // Price change vs the LAST time each item was purchased (matched by
        // normalized description across the user's prior invoices). Flag > ±1%.
        const norm = (x) => (x || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
        const curDate = inv.issuedDate || '';
        const allInv = await db.invoices.where('userId').equals(inv.userId).toArray();
        const dateOf = {}; allInv.forEach((x) => { dateOf[x.invoiceId] = x.issuedDate || ''; });
        const allLines = await db.lineItems.where('userId').equals(inv.userId).toArray();
        const priors = {};
        for (const l of allLines) {
          if (l.invoiceId === id) continue;
          const d = norm(l.description); if (!d || !(l.unitPriceCents > 0)) continue;
          const dt = dateOf[l.invoiceId] || '';
          if (dt > curDate) continue; // only prior/earlier purchases
          if (!priors[d] || dt >= priors[d].date) priors[d] = { date: dt, unitPriceCents: l.unitPriceCents };
        }
        const linesWithDelta = lines.map((li) => {
          const prior = priors[norm(li.description)];
          let pct = null;
          if (prior && prior.unitPriceCents > 0 && li.unitPriceCents > 0) {
            pct = ((li.unitPriceCents - prior.unitPriceCents) / prior.unitPriceCents) * 100;
          }
          return { ...li, _pct: pct, _priorCents: prior ? prior.unitPriceCents : null };
        });

        if (!cancelled) setSt({ loading: false, inv, vendor, lines: linesWithDelta, vendorName: vendorName(vendor?.name), currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) { if (!cancelled) setSt({ loading: false, error: String(e?.message || e) }); }
    })();
    return () => { cancelled = true; };
  }, [id, nonce]);

  // Look up the cached original scan blob. Pre-existing invoices (scanned
  // before v8) have no row, so we show a polite placeholder instead.
  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let revoke = null;
    (async () => {
      try {
        const row = await window.kanjo?.ocr?.getReceiptImage?.(id);
        if (cancelled) return;
        if (!row || !row.blob) { setImageMissing(true); return; }
        const url = URL.createObjectURL(row.blob);
        revoke = url;
        setImageUrl(url);
      } catch (e) {
        console.warn('[InvoiceDetail] image load failed', e);
        setImageMissing(true);
      }
    })();
    return () => { cancelled = true; if (revoke) try { URL.revokeObjectURL(revoke); } catch {} };
  }, [id]);

  if (st.loading) return <KShell dark={dark} tab="receipts" persona={persona}><KHeader back title="Receipt" /><div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>Loading…</div></KShell>;
  if (st.notFound || st.error) return <KShell dark={dark} tab="receipts" persona={persona}><KHeader back title="Receipt" /><Empty title="Receipt not found" body={st.error || 'It may have been removed.'} /></KShell>;

  const { inv, lines, currency, locale } = st;
  // A scanned receipt is dated within the current/previous week and never in the
  // future — flag a misread issue date (future, or far in the past from an
  // account/resale number taken for a date) so it can be corrected here.
  const invDateClass = window.kanjo?.ocr?.classifyIssueDate?.(inv.issuedDate) || { status: 'unparseable', daysAgo: null };
  const invDateFlagged = invDateClass.status === 'future' || invDateClass.status === 'ancient' || invDateClass.status === 'stale';
  const M = (c) => fmtMoneyExact(c || 0, currency, locale);
  const pay = window.kanjo.cashflow.payments.invoicePayment(inv, st.vendor);
  const paySub =
    pay.basis === 'manual' ? 'Set manually' :
    pay.basis === 'card' ? 'Credit card — paid at purchase' :
    pay.basis === 'check_on_delivery' ? 'Check — paid on delivery' :
    pay.basis === 'statement' ? (pay.paid ? `Statement — paid ~${pay.paidDate}` : `Statement — pay by ${pay.expectedPayIso}`) :
    pay.basis === 'unset' ? 'No payment terms set for this vendor' :
    'On account';
  const totalRow = (label, value, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: bold ? 16 : 14, fontWeight: bold ? 700 : 500, color: bold ? 'var(--on)' : 'var(--on-soft)' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );

  const itemCount = lines.length;
  const photoThumb = imageUrl ? (
    <button onClick={() => setPhotoOpen(true)} aria-label="View original receipt photo"
      style={{ width: 32, height: 32, padding: 0, borderRadius: 8, overflow: 'hidden',
        border: '1px solid var(--rule)', background: 'var(--surface-3)', cursor: 'pointer' }}>
      <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
    </button>
  ) : null;

  return (
    <KShell dark={dark} tab="receipts" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24, position: 'relative' }}
        onClick={() => { if (menuLineId) setMenuLineId(null); }}>
        <KHeader back title={st.vendorName} right={photoThumb} />
        <div style={{ padding: '0 20px' }}>
          <div style={{ fontSize: 13, color: 'var(--on-soft)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {inv.invoiceNumber ? <span>Invoice #{inv.invoiceNumber} ·</span> : null}
            {editingDate ? (
              <input
                type="date"
                autoFocus
                defaultValue={window.kanjo?.ocr?.parseISODate?.(inv.issuedDate) || ''}
                max={window.kanjo?.ocr?.todayISO?.() || ''}
                onBlur={(e) => onSaveDate(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                aria-label="Invoice date"
                disabled={busy}
                style={{ border: '1px solid var(--rule-2)', borderRadius: 6, padding: '2px 6px', fontSize: 13,
                         fontFamily: 'inherit', color: 'var(--ink)', background: 'var(--surface)',
                         colorScheme: dark ? 'dark' : 'light' }}
              />
            ) : (
              <button onClick={() => setEditingDate(true)} aria-label="Edit invoice date"
                      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                               fontSize: 13, fontWeight: invDateFlagged ? 700 : 500,
                               color: invDateFlagged ? 'var(--warning,#c9941f)' : 'var(--on-soft)',
                               textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                {inv.issuedDate || 'Add date'}
              </button>
            )}
            {inv.dueDate ? <span>· due {inv.dueDate}</span> : null}
          </div>

          {/* Filed-under line — lets the owner refile a misread receipt under the
              right (or a de-duplicated) vendor without re-scanning it. */}
          <div style={{ fontSize: 12.5, color: 'var(--on-dim)', marginBottom: 16, marginTop: -8 }}>
            Filed under <strong style={{ color: 'var(--on-soft)', fontWeight: 600 }}>{st.vendorName}</strong>
            {' · '}
            <button onClick={() => setReassignOpen(true)} disabled={busy}
              style={{ background: 'none', border: 'none', padding: 0, cursor: busy ? 'default' : 'pointer',
                       color: 'var(--brand)', fontSize: 12.5, fontWeight: 600 }}>
              Change vendor
            </button>
          </div>

          {invDateFlagged && (
            <WarningBanner style={{ marginBottom: 16 }}>
              {invDateClass.status === 'future' ? (
                <>This receipt is dated <strong>{inv.issuedDate}</strong>, which is in the future — a receipt can't be. It was likely misread when scanned; tap the date above to correct it.</>
              ) : invDateClass.status === 'ancient' ? (
                <>This receipt is dated <strong>{inv.issuedDate}</strong> — over {Math.floor(invDateClass.daysAgo / 30)} months ago, so it sits outside every report. A receipt that old is almost always a misread year; tap the date above to correct it.</>
              ) : (
                <>This receipt is dated <strong>{inv.issuedDate}</strong> — {invDateClass.daysAgo} days ago, more than a week back. If that's not right, tap the date above to correct it.</>
              )}
            </WarningBanner>
          )}

          {inv.isDuplicate && (
            <div role="alert" style={{
              marginBottom: 16, padding: '14px 16px', borderRadius: 12,
              background: 'var(--warning-bg)', border: '1px solid var(--warning)',
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>⚠ Flagged as a duplicate</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45, marginBottom: 12 }}>
                This receipt matches another already on your books. Duplicates are excluded from food-cost and 7/30-day reports until you keep them.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onDiscardDuplicate} disabled={busy}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: 'none', background: 'var(--danger-fill)', color: 'var(--on-danger-fill)', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                  Discard
                </button>
                <button onClick={onKeepDuplicate} disabled={busy}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                  Keep it
                </button>
              </div>
            </div>
          )}

          {/* Payment status — derived from the vendor's policy, with manual override */}
          <div style={{ marginBottom: 16 }}>
            <div className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: pay.paid ? 'var(--positive)' : 'var(--warning)' }}>
                  {pay.paid ? 'Paid' : 'Unpaid'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{paySub}</div>
              </div>
              <button onClick={() => onSetPaid(pay.paid ? 'unpaid' : 'paid')} disabled={busy} style={{
                padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
              }}>{pay.paid ? 'Mark unpaid' : 'Mark paid'}</button>
            </div>
            {inv.paidOverride && (
              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 6, paddingLeft: 4 }}>
                Manually overridden ·{' '}
                <button onClick={() => onSetPaid(null)} style={{ background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', fontSize: 11.5, padding: 0 }}>
                  revert to {st.vendor?.paymentMethod ? 'vendor rule' : 'default'}
                </button>
              </div>
            )}
          </div>

          {/* Sticky summary strip — Total · items · tax. The first thing the
              owner verifies against the paper they hold. */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 5,
            margin: '0 -20px 16px', padding: '10px 20px',
            background: 'var(--surface)',
            borderTop: '1px solid var(--rule)', borderBottom: '1px solid var(--rule)',
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
          }}>
            <span><KMoney cents={inv.totalCents} currency={currency} locale={locale}
                          style={{ fontSize: 20, fontWeight: 700 }}/></span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {itemCount} item{itemCount === 1 ? '' : 's'}
              {inv.taxCents ? <> · tax {M(inv.taxCents)}</> : null}
            </span>
          </div>

          <div>
            {lines.map((li) => {
              if (editingLineId === li.lineItemId) {
                return <EditableLineRow key={li.lineItemId} li={li} M={M} busy={busy}
                                        onCancel={() => setEditingLineId(null)}
                                        onSave={(patch) => onSaveLine(li.lineItemId, patch)} />;
              }
              const pct = li._pct;
              const isUp = pct != null && pct > 0;
              const showDelta = pct != null && Math.abs(pct) >= 0.5;
              return (
                <div key={li.lineItemId}
                     style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8,
                       padding: '10px 0', borderBottom: '1px solid var(--hairline)' }}>
                  <button onClick={() => setEditingLineId(li.lineItemId)}
                          aria-label={`Edit ${li.description || 'line'}`}
                          style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            textAlign: 'left', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <div style={{ minWidth: 0, paddingRight: 12 }}>
                      <div style={{ fontSize: 14, color: 'var(--on)' }}>{li.description || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--on-dim)' }}>
                        {li.quantity || 0}{li.uom ? ` ${li.uom}` : ''} × {M(li.unitPriceCents)}
                        {showDelta && (
                          <span style={{ marginLeft: 6, fontWeight: 600, color: isUp ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)' }}>
                            {isUp ? '▲' : '▼'} {Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}% vs {M(li._priorCents)}
                          </span>
                        )}
                        {pct != null && !showDelta && (
                          <span style={{ marginLeft: 6, fontWeight: 500, color: 'var(--on-dim)' }}>
                            ≈ {M(li._priorCents)}
                          </span>
                        )}
                        {pct == null && (
                          <span style={{ marginLeft: 6, fontWeight: 500, color: 'var(--ink-3)',
                            padding: '1px 5px', borderRadius: 4, background: 'var(--surface-3,#f0f0f3)', fontSize: 10.5 }}>
                            new
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap' }}>{M(li.extendedPriceCents)}</div>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setMenuLineId(menuLineId === li.lineItemId ? null : li.lineItemId); }}
                          aria-label="Row actions" aria-haspopup="menu"
                          style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 6, border: 'none',
                            background: 'transparent', color: 'var(--on-dim)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
                    ⋯
                  </button>
                  {menuLineId === li.lineItemId && (
                    <div role="menu" onClick={(e) => e.stopPropagation()}
                         style={{ position: 'absolute', right: 0, top: '100%', zIndex: 6,
                           background: 'var(--surface,#fff)', border: '1px solid var(--rule)', borderRadius: 10,
                           boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 180, padding: 4 }}>
                      <button onClick={() => { setMenuLineId(null); setEditingLineId(li.lineItemId); }}
                              style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none',
                                background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontSize: 13 }}>
                        Edit qty / price / total
                      </button>
                      <button onClick={async () => {
                        setMenuLineId(null);
                        try {
                          const nextFlagged = !li.flaggedAsMistake;
                          await window.kanjo.ocr.updateLineItem(li.lineItemId, { flaggedAsMistake: nextFlagged ? 1 : 0 });
                          reload();
                        } catch (e) { console.warn('[InvoiceDetail] flag failed', e); }
                      }}
                              style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 6, border: 'none',
                                background: 'transparent', color: li.flaggedAsMistake ? 'var(--ink-3)' : 'var(--danger,#d2563f)',
                                cursor: 'pointer', fontSize: 13 }}>
                        {li.flaggedAsMistake ? 'Clear mistake flag' : 'Flag as OCR mistake'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {!lines.length && <div style={{ fontSize: 13, color: 'var(--on-dim)', padding: '12px 0' }}>No line items were extracted from this receipt.</div>}
          </div>
          <div style={{ marginTop: 16, borderTop: '1px solid var(--hairline)', paddingTop: 12 }}>
            {inv.subtotalCents ? totalRow('Subtotal', M(inv.subtotalCents)) : null}
            {inv.taxCents ? totalRow('Tax', M(inv.taxCents)) : null}
            {inv.freightCents ? totalRow('Freight', M(inv.freightCents)) : null}
            {totalRow('Total', M(inv.totalCents), true)}
          </div>
        </div>
      </div>

      {reassignOpen && (
        <ReassignVendorSheet currentVendorId={inv.vendorId} currentName={st.vendorName} busy={busy}
          onPick={onReassign} onClose={() => setReassignOpen(false)} />
      )}

      {/* Full-screen photo viewer. Native pinch-zoom via touch-action; tap
          anywhere to close. Falls back to "no original on this device" when
          the scan predates v8 or happened on another device. */}
      {photoOpen && (
        <div onClick={() => setPhotoOpen(false)} role="dialog" aria-label="Original receipt photo"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto',
            padding: 16, touchAction: 'pinch-zoom' }}>
          {imageUrl
            ? <img src={imageUrl} alt="Original receipt" style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}/>
            : <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, textAlign: 'center', maxWidth: 320 }}>
                Original photo isn't stored on this device.<br/>
                Future scans will keep a local copy so you can compare against the paper.
              </div>}
        </div>
      )}

      {/* Footer hint about the missing image (only when no thumbnail in header) */}
      {imageMissing && !photoOpen && (
        <button onClick={() => setPhotoOpen(true)}
          style={{ position: 'fixed', bottom: 84, right: 16, zIndex: 50,
            padding: '8px 12px', borderRadius: 999, border: '1px solid var(--rule)',
            background: 'var(--surface)', color: 'var(--ink-2)', fontSize: 11.5, cursor: 'pointer' }}>
          No original photo
        </button>
      )}
    </KShell>
  );
}

// ─── ReassignVendorSheet — refile one receipt under a different vendor ───────
// Lists the owner's other active vendors to pick from, plus a field to file the
// receipt under a brand-new vendor name. The picker reuses fuzzy matching on the
// save side, so typing an existing variant folds in rather than duplicating.
function ReassignVendorSheet({ currentVendorId, currentName, busy, onPick, onClose }) {
  const [vendors, setVendors] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [newName, setNewName] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await window.kanjo.procurement.vendors.listActive();
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setVendors(list.filter((v) => v.vendorId !== currentVendorId));
      } catch (e) { if (!cancelled) setVendors([]); }
    })();
    return () => { cancelled = true; };
  }, [currentVendorId]);

  const needle = q.trim().toLowerCase();
  // Search matches the STORED name while the list shows the display one. In demo
  // mode those differ, but reassigning an invoice is an operating action, not
  // something done during a walkthrough — and matching on the fake name would
  // mean the picker could not find a vendor by what the invoice actually says.
  const shown = (vendors || []).filter((v) => !needle || (v.name || '').toLowerCase().includes(needle));
  const trimmedNew = newName.trim();

  return (
    <div role="dialog" aria-modal="true" aria-label="Change vendor" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480, background: 'var(--surface)', color: 'var(--ink)',
        borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 18px calc(env(safe-area-inset-bottom,0) + 20px)',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Change vendor</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 14 }}>
          Currently filed under {currentName}. Pick another supplier or add a new one.
        </div>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your vendors"
          aria-label="Search vendors"
          style={{ width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', marginBottom: 10 }}/>

        <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
          {vendors == null && <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 2px' }}>Loading…</div>}
          {vendors != null && shown.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 2px' }}>No other vendors match.</div>
          )}
          {shown.map((v) => (
            <button key={v.vendorId} onClick={() => onPick({ vendorId: v.vendorId })} disabled={busy}
              style={{ textAlign: 'left', padding: '11px 12px', borderRadius: 10, cursor: busy ? 'default' : 'pointer',
                       border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14, fontWeight: 600 }}>
              {vendorName(v.name)}
            </button>
          ))}
        </div>

        <div className="label" style={{ marginBottom: 6 }}>Or file under a new vendor</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New vendor name"
            aria-label="New vendor name"
            style={{ flex: 1, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)' }}/>
          <button onClick={() => onPick({ name: trimmedNew })} disabled={busy || !trimmedNew}
            style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', fontSize: 14, fontWeight: 600, cursor: (busy || !trimmedNew) ? 'default' : 'pointer', opacity: (busy || !trimmedNew) ? 0.5 : 1 }}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditableLineRow — inline edit of a saved line item's qty/unit/total ─────
// Editing qty or unit price recomputes the total; editing the total field
// overrides it (so a wrong OCR total — e.g. a "CS" of broccoli priced per lb —
// can be corrected directly).
function EditableLineRow({ li, M, busy, onCancel, onSave }) {
  const [qty, setQty] = React.useState(String(li.quantity ?? ''));
  const [unit, setUnit] = React.useState(((li.unitPriceCents || 0) / 100).toFixed(2));
  const [overrideTotal, setOverrideTotal] = React.useState(false);
  const [total, setTotal] = React.useState(((li.extendedPriceCents || 0) / 100).toFixed(2));

  const computedTotal = ((Number(qty) || 0) * (Math.round((Number(unit) || 0) * 100))) / 100;
  const shownTotal = overrideTotal ? total : computedTotal.toFixed(2);

  const inputStyle = {
    width: '100%', border: '1px solid var(--rule-2)', borderRadius: 8, padding: '6px 8px',
    fontSize: 14, color: 'var(--ink)', background: 'var(--surface)', fontFamily: 'var(--mono)', textAlign: 'right',
  };
  const labelStyle = { fontSize: 10, color: 'var(--on-dim)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.04em' };

  const save = () => {
    const patch = {
      quantity: Number(qty) || 0,
      unitPriceCents: Math.round((Number(unit) || 0) * 100),
    };
    if (overrideTotal) patch.extendedPriceCents = Math.round((Number(total) || 0) * 100);
    onSave(patch);
  };

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--hairline)' }}>
      <div style={{ fontSize: 14, color: 'var(--on)', fontWeight: 500, marginBottom: 8 }}>{li.description || '—'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <input inputMode="decimal" aria-label="Quantity" value={qty}
               onChange={(e) => { setQty(e.target.value); setOverrideTotal(false); }} style={inputStyle} />
        <input inputMode="decimal" aria-label="Unit price" value={unit}
               onChange={(e) => { setUnit(e.target.value); setOverrideTotal(false); }} style={inputStyle} />
        <input inputMode="decimal" aria-label="Line total" value={shownTotal}
               onChange={(e) => { setTotal(e.target.value); setOverrideTotal(true); }}
               style={{ ...inputStyle, fontWeight: 600 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 3 }}>
        <div style={labelStyle}>Qty</div><div style={labelStyle}>Unit</div><div style={labelStyle}>Total</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--brand, #2563eb)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>Save</button>
        <button onClick={onCancel} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink-2)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Price-series helpers ────────────────────────────────────────────────
// Date math is intentionally local (no Date.parse ambiguity): invoice dates are
// stored as 'YYYY-MM-DD' strings, so we build a local Date and group by ISO week
// (Mon-start) or calendar month relative to today.
function parsePriceDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s); return isNaN(d.getTime()) ? null : d;
}
const monthKey = (d) => d.getFullYear() * 12 + d.getMonth();
const avgCents = (arr) => Math.round(arr.reduce((s, c) => s + c, 0) / arr.length);
const pctMove = (oldC, newC) => ((newC - oldC) / oldC) * 100;

// Normalize an OCR line-item description into a grouping key. Beyond casing /
// punctuation, this strips LEADING pack-size & quantity tokens that vary per
// invoice (e.g. "1CS 0LB Broccoli Crowns" vs "Broccoli Crowns") so the same
// product collapses to one ingredient instead of splitting into duplicates.
const PACK_UNIT = /^(cs|case|cases|lb|lbs|oz|ct|cnt|count|ea|each|pc|pcs|pk|pkg|pack|kg|g|gr|gal|doz|dz|bx|box|qt|pt|ml|ltr|l|x|hash)$/;
function normItemName(x) {
  const s = (x || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  const toks = s.split(' ');
  let i = 0;
  // Drop leading measurement-ish tokens: anything containing a digit (1cs, 0lb,
  // 24, 2/5), or a standalone unit word (cs, lb, ct…). Stop at the first word.
  while (i < toks.length && (/\d/.test(toks[i]) || PACK_UNIT.test(toks[i]))) i++;
  const out = toks.slice(i).join(' ').trim();
  return out || s; // never strip everything away
}

// Unit-of-measure buckets + the suspicious-move guard. Kept in sync with
// app/src/lib/procurement/priceUnit.js (this view is a browser-global file and
// can't ES-import that module — same reason normItemName is duplicated above).
const UOM_CANON = {
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  g: 'g', gr: 'g', gram: 'g', grams: 'g',
  cs: 'cs', case: 'cs', cases: 'cs',
  bg: 'bg', bag: 'bg', bags: 'bg',
  bx: 'bx', box: 'bx', boxes: 'bx',
  ct: 'ct', cnt: 'ct', count: 'ct',
  ea: 'ea', each: 'ea', unit: 'ea', units: 'ea', pc: 'ea', pcs: 'ea', piece: 'ea', pieces: 'ea',
  pk: 'pk', pkg: 'pk', pack: 'pk', packs: 'pk', package: 'pk',
  doz: 'doz', dz: 'doz', dozen: 'doz',
  gal: 'gal', gallon: 'gal', gallons: 'gal',
  qt: 'qt', quart: 'qt', quarts: 'qt',
  pt: 'pt', pint: 'pt', pints: 'pt',
  l: 'l', ltr: 'l', liter: 'l', litre: 'l', liters: 'l', litres: 'l',
  ml: 'ml',
};
function normUom(uom) {
  const s = String(uom || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!s) return '';
  return UOM_CANON[s] || s;
}
// Split a product's series by unit ONLY when 2+ distinct populated units appear
// (per-lb AND per-case). A lone blank unit is missing OCR data for the single
// real unit — splitting it would fragment a real series and hide a legit move.
function unitsConflict(uoms) {
  const distinct = new Set();
  for (const u of uoms) { const n = normUom(u); if (n) distinct.add(n); }
  return distinct.size >= 2;
}
const SUSPICIOUS_PCT = 50;
function isSuspiciousMove(oldCents, newCents) {
  if (!(oldCents > 0) || !(newCents > 0)) return true;
  return Math.abs((newCents - oldCents) / oldCents) * 100 > SUSPICIOUS_PCT;
}
// Mirrors lib/procurement/priceUnit.blankSeriesIsAmbiguous (tested there). A
// blank-unit series at a mixed-unit vendor is only really ambiguous when its
// OWN prices straddle magnitude bands; a tight one is like-for-like whatever
// the unread label said.
function blankSeriesIsAmbiguous(centsList) {
  const cents = (centsList || []).filter((c) => c > 0);
  if (cents.length < 2) return false;
  return Math.max(...cents) / Math.min(...cents) > 1 + SUSPICIOUS_PCT / 100;
}

// Build per-item unit-price series (points sorted oldest→newest), matched by
// normalized description across the user's invoices. Loaded once; the timeframe
// views below are pure derivations so switching modes needs no DB round-trip.
async function loadPriceSeries(db, userId) {
  const invoices = await db.invoices.where('userId').equals(userId).toArray();
  const vmap = {};
  for (const vid of [...new Set(invoices.map((i) => i.vendorId).filter(Boolean))]) {
    const v = await db.vendors.get(vid); if (v) vmap[vid] = vendorName(v.name);
  }
  const dateOf = {}; const vendorOf = {};
  invoices.forEach((i) => { dateOf[i.invoiceId] = i.issuedDate || ''; vendorOf[i.invoiceId] = vmap[i.vendorId] || 'Unknown vendor'; });
  const lines = await db.lineItems.where('userId').equals(userId).toArray();
  const byDesc = {};
  for (const l of lines) {
    const d = normItemName(l.description); if (!d || !(l.unitPriceCents > 0)) continue;
    // Excluded points ride along flagged (not dropped) so the fix-it sheet can
    // still list them with an Undo; consumers skip p.excluded for the math.
    (byDesc[d] = byDesc[d] || []).push({
      date: dateOf[l.invoiceId] || '', cents: l.unitPriceCents, vendor: vendorOf[l.invoiceId], label: l.description, uom: l.uom,
      lineItemId: l.lineItemId, quantity: l.quantity, extendedPriceCents: l.extendedPriceCents, excluded: !!l.priceExcluded,
    });
  }
  return Object.keys(byDesc).map((d) => ({
    key: d,
    points: byDesc[d].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
  }));
}

// Movers for the list view — ALWAYS per (item × supplier): Mango at Costco
// only ever compares against Mango at Costco, never Mango at Lam Seafood
// (owner rule 2026-07-11 — cross-supplier deltas are unit/pack noise, and the
// app never recommends switching suppliers anyway). mode:
//   'invoice' → the last purchase vs the previous one at the SAME supplier
//   'month'   → this calendar month's average vs last month's, per supplier
//   'year'    → first purchase this calendar year vs the latest, per supplier;
//               rows carry the year's price points for the inline chart (the
//               only mode that charts — invoice/month are plain lists).
// Unchanged prices (±1%) are hidden. Sorted increases first (biggest rise
// first), then decreases (biggest drop first).
function priceMoversFromSeries(series, mode = 'invoice') {
  const now = new Date();
  const thisYear = now.getFullYear();
  const ck = monthKey(now);
  // Split each item's points into one series per supplier, then (only when that
  // supplier sold it under 2+ different units) into per-unit series — so per-lb
  // never compares against per-case, but a lone blank unit still rides with the
  // single real unit instead of fragmenting a legit series. Each row carries the
  // set of units in its series so the fix-it sheet can reproduce the same scope.
  const perVendor = [];
  for (const s of series) {
    const byVendor = new Map();
    for (const p of s.points) {
      const vendor = p.vendor || 'Unknown vendor';
      if (!byVendor.has(vendor)) byVendor.set(vendor, []);
      byVendor.get(vendor).push(p);
    }
    for (const [vendor, vpts] of byVendor) {
      // Only points still IN price tracking vote — an operator-hidden bad OCR
      // point (often the one with the wrong unit) must not force a split the
      // clean data doesn't justify. Same rule as the lib (which pre-filters
      // priceExcluded before classifyMoves).
      const split = unitsConflict(vpts.filter((p) => !p.excluded).map((p) => p.uom));
      const bySeries = new Map();
      for (const p of vpts) {
        const sk = split ? normUom(p.uom) : '*';
        if (!bySeries.has(sk)) bySeries.set(sk, []);
        bySeries.get(sk).push(p);
      }
      for (const [sk, pts] of bySeries) {
        const seriesUoms = [...new Set(pts.map((p) => normUom(p.uom)))];
        // Blank series under a real conflict: unit-ambiguous ONLY if its own
        // prices straddle magnitude bands. A tight blank series is one coherent
        // unit whose label the OCR never read — comparing inside it is safe.
        const unitAmbiguous = split && sk === ''
          && blankSeriesIsAmbiguous(pts.filter((p) => !p.excluded).map((p) => p.cents));
        perVendor.push({ itemKey: s.key, vendor, pts, seriesUoms, unitAmbiguous });
      }
    }
  }

  const rows = [];
  for (const { itemKey, vendor, pts: history, seriesUoms, unitAmbiguous } of perVendor) {
    // Price-excluded points (bad OCR quantities the operator hid) don't take
    // part in any comparison, but the full history rides on the row so the
    // fix-it sheet can show and un-hide them.
    const pts = history.filter((p) => !p.excluded);
    let oldCents, newCents, latest, chartPts = null;
    if (mode === 'invoice') {
      if (pts.length < 2) continue;
      latest = pts[pts.length - 1]; const prev = pts[pts.length - 2];
      oldCents = prev.cents; newCents = latest.cents;
    } else if (mode === 'month') {
      const cur = [], prv = [];
      for (const p of pts) {
        const dt = parsePriceDate(p.date); if (!dt) continue;
        if (monthKey(dt) === ck) cur.push(p); else if (monthKey(dt) === ck - 1) prv.push(p);
      }
      if (!cur.length || !prv.length) continue;
      oldCents = avgCents(prv.map((p) => p.cents));
      newCents = avgCents(cur.map((p) => p.cents));
      latest = cur[cur.length - 1];
    } else { // 'year'
      const yr = pts.filter((p) => { const dt = parsePriceDate(p.date); return dt && dt.getFullYear() === thisYear; });
      if (yr.length < 2) continue;
      latest = yr[yr.length - 1];
      oldCents = yr[0].cents; newCents = latest.cents;
      chartPts = yr;
    }
    if (!(oldCents > 0)) continue;
    const pct = pctMove(oldCents, newCents);
    if (Math.abs(pct) < 1) continue; // unchanged → not shown
    // A move over ±50% is almost always a pack/unit mismatch or an OCR misread,
    // not a real price change — held for review, never a headline (owner rule).
    // A unit-ambiguous series (blank unit at a mixed-unit vendor) is ALWAYS
    // review, whatever its size — its points could be either unit.
    // Two different reasons to hold a row back, and they need different words:
    // a >±50% swing is a number nobody should act on, while a unit-ambiguous
    // series is a perfectly ordinary move we can't prove is like-for-like.
    const bigMove = isSuspiciousMove(oldCents, newCents);
    const suspect = unitAmbiguous || bigMove;
    const reviewReason = bigMove ? 'move' : unitAmbiguous ? 'unit' : null;
    rows.push({ label: latest.label, vendor, itemKey, oldCents, newCents, pct, date: latest.date, points: chartPts, history, suspect, reviewReason, seriesUoms });
  }
  const clean = rows.filter((r) => !r.suspect);
  const review = rows.filter((r) => r.suspect).sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  if (mode === 'invoice') {
    // Grouped by supplier, biggest increase down to biggest decrease within
    // each — the rule lives in the lib so it's covered by tests.
    return { rows: window.kanjo.procurement.priceMovers.sortByVendorThenMove(clean), review };
  } else {
    clean.sort((a, b) => {
      if ((a.pct > 0) !== (b.pct > 0)) return a.pct > 0 ? -1 : 1; // increases before decreases
      return a.pct > 0 ? b.pct - a.pct : a.pct - b.pct;           // biggest rise / biggest drop first
    });
  }
  return { rows: clean, review };
}

// Items whose unit price moved >±1% since the prior purchase. Shared by the
// Reports tile count so it always agrees with the screen's default view.
async function computePriceMovers(db, userId) {
  const series = await loadPriceSeries(db, userId);
  return priceMoversFromSeries(series, 'invoice');
}

// Format a ±pct with a sensible number of decimals (1 below 10%, whole above).
const fmtPct = (pct) => `${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`;
const shortDate = (s) => { const d = parsePriceDate(s); return d ? `${d.getMonth() + 1}/${d.getDate()}` : ''; };

// Price-over-time line chart: x = invoice date (true time spacing), y = unit
// price. Scales uniformly (preserveAspectRatio meet) so dots stay round. A flat
// series is nudged ±1¢ so the line renders mid-height instead of on an edge.
function PriceLineChart({ points, color, height = 92 }) {
  const W = 320, H = 100, padL = 8, padR = 8, padT = 12, padB = 16;
  const ts = points.map((p) => { const d = parsePriceDate(p.date); return d ? d.getTime() : 0; });
  const minX = Math.min(...ts), maxX = Math.max(...ts);
  const cents = points.map((p) => p.cents);
  let minY = Math.min(...cents), maxY = Math.max(...cents);
  if (minY === maxY) { minY -= 1; maxY += 1; }
  const spanX = (maxX - minX) || 1, spanY = (maxY - minY) || 1;
  const px = (t) => padL + ((t - minX) / spanX) * (W - padL - padR);
  const py = (c) => padT + (1 - (c - minY) / spanY) * (H - padT - padB);
  const path = points.map((p, i) => `${px(ts[i]).toFixed(1)},${py(p.cents).toFixed(1)}`).join(' L');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Unit price over time">
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--hairline)" strokeWidth="1"/>
      <path d={`M${path}`} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
      {points.map((p, i) => <circle key={i} cx={px(ts[i])} cy={py(p.cents)} r="2.6" fill={color}/>)}
    </svg>
  );
}

// One mover row: header (name · vendor · old → new · ±pct) and, when the item
// has ≥2 prices in the trailing 30 days, the price-over-time chart inline with
// a footer anchoring the time axis (first date … high/low … last date).
// `hideVendor` drops the supplier from the subline when a vendor heading above
// already says it — repeating it on every row is what made the list dense.
function PriceMoverRow({ item, M, onOpen, hideVendor = false }) {
  const up = item.pct > 0;
  const color = up ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)';
  const pts = item.points;
  const cents = pts ? pts.map((p) => p.cents) : null;
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen && onOpen(item)}
      onKeyDown={(e) => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(item); } }}
      aria-label={`${item.label} — inspect price points`}
      style={{ padding: '14px 4px', borderBottom: '1px solid var(--hairline)', cursor: onOpen ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, paddingRight: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</div>
          <div style={{ fontSize: 12.5, color: 'var(--on-soft)' }}>{hideVendor ? '' : `${item.vendor} · `}{M(item.oldCents)} → {M(item.newCents)}{item.date ? ` · ${shortDate(item.date)}` : ''}</div>
          {item.reviewReason && (
            <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 3 }}>
              {item.reviewReason === 'move'
                ? t('prices.reviewWhyMove', 'Swing over ±50% — check for a pack/unit mismatch or a misread')
                : t('prices.reviewWhyUnit', 'Unit not recorded, and this supplier bills it more than one way')}
            </div>
          )}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', color }}>
          {up ? '▲' : '▼'} {fmtPct(item.pct)}
        </div>
      </div>
      {pts && (
        <>
          <div style={{ marginTop: 8 }}><PriceLineChart points={pts} color={color} /></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--on-dim)', marginTop: 2 }}>
            <span>{shortDate(pts[0].date)}</span>
            <span>high {M(Math.max(...cents))} · low {M(Math.min(...cents))}</span>
            <span>{shortDate(pts[pts.length - 1].date)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// Drill-in sheet for one item × supplier: every recorded price point (newest
// first), with the two repair tools for bad OCR reads — "Fix qty" (keeps the
// printed line total, recomputes the unit price) and "Hide" (excludes the
// point from price tracking only; the spend stays in COGS). Hidden points stay
// listed, struck through, so hiding is reversible.
function PricePointsSheet({ row, M, onClose, onChanged }) {
  const [editing, setEditing] = React.useState(null); // lineItemId being edited
  const [qty, setQty] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  if (!row) return null;
  const pts = [...row.history].reverse().slice(0, 12); // newest first
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); setEditing(null); await onChanged(); }
    catch (e) { console.warn('[prices] point update failed', e); }
    finally { setBusy(false); }
  };
  const btn = { padding: '7px 12px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0 };
  return (
    <Sheet open onClose={onClose} title={`${row.label} · ${row.vendor}`}>
      <div style={{ fontSize: 12.5, color: 'var(--on-soft)', lineHeight: 1.5, margin: '0 0 12px' }}>
        {t('prices.fixHint', 'A wild price is usually a misread quantity (e.g. 2 cases read as 1). Fix the quantity and the unit price recomputes from the printed line total — or hide the point from price tracking; the spend itself stays in your cost reports.')}
      </div>
      {pts.map((p) => (
        <div key={p.lineItemId} style={{ padding: '11px 0', borderBottom: '1px solid var(--hairline)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0, textDecoration: p.excluded ? 'line-through' : 'none', color: p.excluded ? 'var(--on-dim)' : 'var(--on)' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{shortDate(p.date)} · {Number(p.quantity) || '?'} × {M(p.cents)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {p.label}{p.extendedPriceCents > 0 ? ` · ${t('prices.lineTotal', 'line total')} ${M(p.extendedPriceCents)}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {!p.excluded && editing !== p.lineItemId && (
                <button disabled={busy} style={btn} onClick={() => { setEditing(p.lineItemId); setQty(String(p.quantity || '')); }}>
                  {t('prices.fixQty', 'Fix qty')}
                </button>
              )}
              <button disabled={busy} style={btn} onClick={() => act(() => window.kanjo.ocr.setLineItemPriceExclusion(p.lineItemId, !p.excluded))}>
                {p.excluded ? t('prices.show', 'Show') : t('prices.hide', 'Hide')}
              </button>
            </div>
          </div>
          {editing === p.lineItemId && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
              <input value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal"
                aria-label={t('prices.trueQty', 'True quantity')} placeholder={t('prices.trueQty', 'True quantity')}
                style={{ flex: 1, minWidth: 0, padding: '9px 11px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }}/>
              <button disabled={busy || !(Number(qty) > 0)} style={{ ...btn, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', border: 'none', opacity: busy || !(Number(qty) > 0) ? 0.6 : 1 }}
                onClick={() => act(() => window.kanjo.ocr.fixLineItemQuantity(editing, Number(qty)))}>
                {t('common.save', 'Save')}
              </button>
              <button disabled={busy} style={btn} onClick={() => setEditing(null)}>{t('common.cancel', 'Cancel')}</button>
            </div>
          )}
        </div>
      ))}
    </Sheet>
  );
}

// ─── Price changes — what went up, what went down, per supplier ───────────────
const PRICE_MODES = [
  { id: 'invoice', l: 'Invoice',   sub: 'Last purchase vs the previous one · same supplier' },
  { id: 'month',   l: 'Month',     sub: "This month's average vs last month · same supplier" },
  { id: 'year',    l: 'This year', sub: 'First purchase this year vs the latest · same supplier' },
];

function PriceChanges({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true, series: [], currency: 'USD', locale: 'en-US' });
  const [mode, setMode] = React.useState('invoice');
  const [sel, setSel] = React.useState(null); // { itemKey, vendor, label } — fix-it sheet target
  const load = React.useCallback(async () => {
    if (!window.kanjo) return;
    try {
      const claims = await window.kanjo.auth.currentClaims().catch(() => null);
      if (!claims) { setSt({ loading: false, series: [] }); return; }
      const userId = claims.sub;
      const db = window.kanjo.db;
      const series = await loadPriceSeries(db, userId);
      const profile = await db.userProfile.get(userId).catch(() => null);
      setSt({ loading: false, series, currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
    } catch (e) { setSt({ loading: false, series: [], error: String(e?.message || e) }); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const M = (c) => fmtMoneyExact(c, st.currency, st.locale);
  const { rows, review } = React.useMemo(() => priceMoversFromSeries(st.series, mode), [st.series, mode]);
  // The sheet re-derives its history from the FRESH series after every fix, so
  // it stays open and correct even when the fix removes the row from `rows`
  // (a repaired point usually drops the move below the ±1% floor).
  const selRow = React.useMemo(() => {
    if (!sel) return null;
    const s = st.series.find((x) => x.key === sel.itemKey);
    // Scope the sheet to the SAME unit-series the row came from — otherwise a
    // per-lb row would open a sheet with the vendor's per-case points mixed in,
    // and hiding one there would yank a legit other-unit point. An empty/absent
    // uom set means the series wasn't unit-split (single real unit) → vendor only.
    const uomSet = new Set(sel.seriesUoms || []);
    const history = s ? s.points.filter((p) =>
      (p.vendor || 'Unknown vendor') === sel.vendor
      && (uomSet.size === 0 || uomSet.has(normUom(p.uom)))) : [];
    return history.length ? { label: sel.label, vendor: sel.vendor, history } : null;
  }, [sel, st.series]);
  const sub = (PRICE_MODES.find((m) => m.id === mode) || PRICE_MODES[0]).sub;
  // Two different groupings. Month/year group by DIRECTION (all increases, then
  // all decreases) across every supplier. Invoice groups by SUPPLIER, each
  // vendor's block running from its biggest increase down to its biggest
  // decrease — so the vendor name moves into a heading and off every row.
  const grouped = mode !== 'invoice';
  const firstDecrease = grouped ? rows.findIndex((r) => r.pct < 0) : -1;

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back big title="Price changes" subtitle={sub} right={<PersonaChip persona={persona}/>}/>

        {/* Timeframe selector */}
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--surface-3)', borderRadius: 12 }}>
            {PRICE_MODES.map((t) => (
              <button key={t.id} onClick={() => setMode(t.id)} style={{
                flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none',
                background: mode === t.id ? 'var(--surface)' : 'transparent',
                fontSize: 13, fontWeight: mode === t.id ? 600 : 500,
                color: mode === t.id ? 'var(--ink)' : 'var(--ink-3)',
                cursor: 'pointer',
                boxShadow: mode === t.id ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
              }}>{t.l}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: '0 16px' }}>
          {st.loading && <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>Loading…</div>}
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {!st.loading && !st.error && rows.length === 0 && review.length === 0 && (
            <Empty title="No price changes in this view"
                   body={mode === 'invoice'
                     ? "Once you've bought the same item twice from the same supplier, any move over ±1% shows up here."
                     : mode === 'month'
                       ? 'No item was bought from the same supplier in both this month and last month yet.'
                       : 'No item has two purchases from the same supplier this year yet.'} />
          )}
          {/* Month/year: increases (biggest rise first), then decreases (biggest
              drop first). Invoice: one block per supplier, biggest increase down
              to biggest decrease. Only the This-year view charts (the year's
              price path); Invoice and Month are plain lists — the numbers ARE
              the story. */}
          {rows.map((r, i) => (
            <React.Fragment key={i}>
              {grouped && i === 0 && r.pct > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: 'var(--danger,#d2563f)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Increases</div>
              )}
              {i === firstDecrease && (
                <div style={{ marginTop: i === 0 ? 4 : 20, fontSize: 12, fontWeight: 700, color: 'var(--positive,#2e9e5b)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Decreases</div>
              )}
              {!grouped && (i === 0 || rows[i - 1].vendor !== r.vendor) && (
                <div style={{ marginTop: i === 0 ? 4 : 22, marginBottom: 2, fontSize: 12, fontWeight: 700,
                  color: 'var(--on-soft)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.vendor}</div>
              )}
              <PriceMoverRow item={r} M={M} hideVendor={!grouped}
                onOpen={(it) => setSel({ itemKey: it.itemKey, vendor: it.vendor, label: it.label, seriesUoms: it.seriesUoms })} />
            </React.Fragment>
          ))}
          {/* Suspect moves (>±50%): almost always a pack/unit mismatch or an OCR
              misread. Held out of the headline list, but shown here so the owner
              can tap in and fix the bad point — the fix-it sheet is the whole
              point of surfacing them. */}
          {!st.loading && !st.error && review.length > 0 && (
            <>
              <div style={{ marginTop: rows.length > 0 ? 24 : 4, fontSize: 12, fontWeight: 700, color: 'var(--warning,#E0962F)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('prices.needsReview', 'Needs review')}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', margin: '4px 0 8px', lineHeight: 1.5 }}>
                {t('prices.reviewHint2', "Held back because the number can't be trusted yet — either the swing is too big to be a real price change, or the supplier billed this item under more than one unit so we can't prove the comparison is like-for-like. Each row says which. Tap to check the price points.")}
              </div>
              {review.map((r, i) => (
                <PriceMoverRow key={`rev-${i}`} item={r} M={M} onOpen={(it) => setSel({ itemKey: it.itemKey, vendor: it.vendor, label: it.label, seriesUoms: it.seriesUoms })} />
              ))}
            </>
          )}
          {!st.loading && !st.error && (rows.length > 0 || review.length > 0) && (
            <div style={{ fontSize: 11.5, color: 'var(--on-dim)', padding: '12px 4px', lineHeight: 1.5 }}>
              {t('prices.tapHint', 'Wrong price? Tap an item to inspect its price points — fix a misread quantity or hide a bad point.')}
            </div>
          )}
        </div>
      </div>
      {selRow && <PricePointsSheet row={selRow} M={M} onClose={() => setSel(null)} onChanged={load} />}
    </KShell>
  );
}

// ─── Price check — "what did I last pay for X, and where?" ────────────────
// Search-first lookup over the user's own scanned line items (NOT the catalog
// price book), so it surfaces every ingredient that's ever appeared on an
// invoice, with the most-recent price PER vendor — the cheapest first. Built
// for standing in a store deciding whether the price in front of you is good.
function ingredientVendorPrices(series) {
  return series.map((s) => {
    const byVendor = {};
    for (const p of s.points) {              // points are date-ascending
      if (p.excluded) continue;              // hidden from price tracking
      const v = p.vendor || 'Unknown vendor';
      const cur = byVendor[v];
      if (!cur || (p.date || '') >= (cur.date || '')) byVendor[v] = { vendor: v, cents: p.cents, date: p.date };
    }
    const vendors = Object.values(byVendor).sort((a, b) => a.cents - b.cents);
    const newest = s.points.reduce((acc, p) => ((p.date || '') >= (acc.date || '') ? p : acc), s.points[0]);
    return { key: s.key, label: newest.label, vendors, bestCents: vendors[0] ? vendors[0].cents : null, sources: vendors.length };
  }).sort((a, b) => a.label.localeCompare(b.label));
}

function PriceCheck({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true, series: [], currency: 'USD', locale: 'en-US' });
  const [q, setQ] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, series: [] }); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        const series = await loadPriceSeries(db, userId);
        const profile = await db.userProfile.get(userId).catch(() => null);
        if (!cancelled) setSt({ loading: false, series, currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) { if (!cancelled) setSt({ loading: false, series: [], error: String(e?.message || e) }); }
    })();
    return () => { cancelled = true; };
  }, []);

  const M = (c) => fmtMoneyExact(c, st.currency, st.locale);
  const query = q.trim().toLowerCase();
  // Browse list (no query): one entry per ingredient, alphabetical.
  const browse = React.useMemo(() => ingredientVendorPrices(st.series), [st.series]);
  // Search: pool EVERY matching line item (across description variants) and keep
  // the most-recent purchase per vendor — one row per supplier, cheapest first.
  const vendorMatches = React.useMemo(() => {
    if (!query) return [];
    const byVendor = {};
    for (const s of st.series) {
      for (const p of s.points) {
        if (!p.label.toLowerCase().includes(query)) continue;
        const v = p.vendor || 'Unknown vendor';
        const cur = byVendor[v];
        if (!cur || (p.date || '') >= (cur.date || '')) byVendor[v] = { vendor: v, cents: p.cents, date: p.date, label: p.label };
      }
    }
    return Object.values(byVendor).sort((a, b) => a.cents - b.cents);
  }, [st.series, query]);

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back big title="Price check" subtitle="Last price you paid, by source" right={<PersonaChip persona={persona}/>}/>

        {/* Search */}
        <div style={{ padding: '0 16px 14px', position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', background: 'var(--surface-3)', borderRadius: 12 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
            <input
              value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Search an ingredient — broccoli, carrots…"
              aria-label="Search ingredient"
              style={{ flex: 1, padding: '12px 0', fontSize: 15, border: 'none', background: 'transparent', color: 'var(--ink)', outline: 'none' }}
            />
            {q && <button onClick={() => setQ('')} aria-label="Clear search" style={{ border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 18, cursor: 'pointer', padding: 4 }}>×</button>}
          </div>
        </div>

        <div style={{ padding: '0 16px' }}>
          {st.loading && <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>Loading…</div>}
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {!st.loading && !st.error && st.series.length === 0 && (
            <Empty title="No scanned items yet" body="Scan a few receipts and the prices you've paid will be searchable here." />
          )}
          {!st.loading && !st.error && query && vendorMatches.length === 0 && (
            <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>No purchases match “{q}”.</div>
          )}

          {/* No search → compact alphabetical browse list (tap to search). */}
          {!query && browse.map((it) => (
            <button key={it.key} onClick={() => setQ(it.label)} style={{
              width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              padding: '13px 4px', borderBottom: '1px solid var(--hairline)', background: 'transparent', border: 'none', cursor: 'pointer',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</div>
                <div style={{ fontSize: 12, color: 'var(--on-dim)', marginTop: 2 }}>{it.sources} source{it.sources === 1 ? '' : 's'}</div>
              </div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap' }}>from {M(it.bestCents)}</div>
            </button>
          ))}

          {/* Search → one row per supplier, cheapest first. The exact item each
              vendor sold is shown as a sub-label (denominations vary by store). */}
          {query && vendorMatches.length > 0 && (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--on-dim)', padding: '4px 4px 8px' }}>
                {vendorMatches.length} source{vendorMatches.length === 1 ? '' : 's'} for “{q}”
              </div>
              {vendorMatches.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 4px', borderBottom: '1px solid var(--hairline)' }}>
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: 'var(--surface-3)', color: 'var(--ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 }}>{(v.vendor || '?')[0].toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.vendor}</span>
                        {i === 0 && vendorMatches.length > 1 && <span className="pill positive" style={{ fontSize: 10, padding: '2px 7px' }}>Cheapest</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--on-dim)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.label}</div>
                      {v.date && <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 1 }}>Last bought {v.date}</div>}
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 16, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap' }}>{M(v.cents)}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

// ─── Vendor spend — this month vs last, ranked, with a 3-month per-vendor chart ─
const ymOf = (iso) => (iso || '').slice(0, 7);                  // 'YYYY-MM'
function shiftYm(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
const ymLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short' }); };

// Inline 3-bar chart of a vendor's monthly spend (heights relative to the max).
function VendorMonthsChart({ months, M, onCanvas = false }) {
  const max = Math.max(1, ...months.map((x) => x.cents));
  // Shared between an in-card use (dark labels) and an on-canvas use (light
  // labels) — `onCanvas` picks the readable pair for the dark purple canvas.
  const amtColor = onCanvas ? 'var(--on-soft)' : 'var(--ink-2)';
  const monColor = onCanvas ? 'var(--on-dim)' : 'var(--ink-3)';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 96, padding: '10px 2px 0' }}>
      {months.map((x) => (
        <div key={x.ym} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div className="mono" style={{ fontSize: 11, color: amtColor }}>{M(x.cents)}</div>
          <div style={{ width: '100%', display: 'flex', alignItems: 'flex-end', height: 56 }}>
            <div style={{ width: '100%', height: `${Math.max(2, (x.cents / max) * 100)}%`, background: 'var(--brand)', borderRadius: '4px 4px 0 0', opacity: x.cents ? 1 : 0.25 }}/>
          </div>
          <div style={{ fontSize: 11, color: monColor }}>{ymLabel(x.ym)}</div>
        </div>
      ))}
    </div>
  );
}

function VendorSpend({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true, vendors: [] });
  const [sel, setSel] = React.useState(null);   // expanded vendorId
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, vendors: [] }); return; }
        const userId = claims.sub;
        const db = window.kanjo.db;
        const today = localToday();
        const m0 = today.slice(0, 7);
        const m1 = shiftYm(m0, -1);
        const months3 = [shiftYm(m0, -2), m1, m0];

        const invoices = (await db.invoices.where('userId').equals(userId).toArray()).filter((i) => !i.isDuplicate);
        const vmap = {};
        for (const vid of [...new Set(invoices.map((i) => i.vendorId).filter(Boolean))]) {
          const v = await db.vendors.get(vid); if (v) vmap[vid] = vendorName(v.name);
        }
        const byVendor = {};
        let curTotal = 0, lastTotal = 0;
        for (const i of invoices) {
          const ym = ymOf(i.issuedDate); if (!ym) continue;
          const vid = i.vendorId || '__none';
          const amt = i.totalCents || 0;
          (byVendor[vid] = byVendor[vid] || {})[ym] = (byVendor[vid][ym] || 0) + amt;
          if (ym === m0) curTotal += amt; else if (ym === m1) lastTotal += amt;
        }
        const vendors = Object.keys(byVendor).map((vid) => {
          const mm = byVendor[vid];
          const cur = mm[m0] || 0, last = mm[m1] || 0;
          return {
            vendorId: vid,
            name: vid === '__none' ? 'Unknown vendor' : (vmap[vid] || 'Unknown vendor'),
            cur, last,
            pct: last > 0 ? ((cur - last) / last) * 100 : null,
            isNew: last === 0 && cur > 0,
            months: months3.map((ym) => ({ ym, cents: mm[ym] || 0 })),
          };
        }).filter((v) => v.cur > 0 || v.last > 0).sort((a, b) => b.cur - a.cur);

        const profile = await db.userProfile.get(userId).catch(() => null);
        if (!cancelled) setSt({
          loading: false, vendors, curTotal, lastTotal, m0, m1,
          currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US',
        });
      } catch (e) { if (!cancelled) setSt({ loading: false, vendors: [], error: String(e?.message || e) }); }
    })();
    return () => { cancelled = true; };
  }, []);

  const M = (c) => fmtMoneyM(c, st.currency, st.locale);
  const totalPct = st.lastTotal > 0 ? ((st.curTotal - st.lastTotal) / st.lastTotal) * 100 : null;

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back big title="Vendor spend"
          subtitle={st.loading ? null : `${ymLabel(st.m0)} vs ${ymLabel(st.m1)}`}
          right={<PersonaChip persona={persona}/>}/>

        <div style={{ padding: '0 16px' }}>
          {st.loading && <div style={{ color: 'var(--on-dim)', fontSize: 14, padding: 24, textAlign: 'center' }}>Loading…</div>}
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {!st.loading && !st.error && st.vendors.length === 0 && (
            <Empty title="No vendor spend yet" body="Scan invoices and your spend by supplier shows up here, this month vs last." />
          )}

          {!st.loading && !st.error && st.vendors.length > 0 && (
            <>
              {/* Hero total */}
              <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                <div className="label">Spent this month</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
                  <div className="mono" style={{ fontSize: 28, fontWeight: 600 }}>{M(st.curTotal)}</div>
                  {totalPct != null && (
                    <span style={{ fontSize: 14, fontWeight: 700, color: totalPct > 0 ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)' }}>
                      {totalPct > 0 ? '▲' : '▼'} {Math.abs(totalPct).toFixed(Math.abs(totalPct) < 10 ? 1 : 0)}%
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{ymLabel(st.m1)}: {M(st.lastTotal)}</div>
              </div>

              <div className="label" style={{ marginBottom: 6, paddingLeft: 4 }}>{st.vendors.length} vendor{st.vendors.length === 1 ? '' : 's'} · tap for 3-month trend</div>
              {st.vendors.map((v) => {
                const open = sel === v.vendorId;
                return (
                  <div key={v.vendorId} style={{ borderBottom: '1px solid var(--hairline)' }}>
                    <button onClick={() => setSel(open ? null : v.vendorId)}
                      style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 4px', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vendorName(v.name)}</div>
                        <div style={{ fontSize: 12.5, color: 'var(--on-dim)', marginTop: 2 }}>{ymLabel(st.m1)}: {M(v.last)}</div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)' }}>{M(v.cur)}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: v.isNew ? 'var(--on-dim)' : v.pct > 0 ? 'var(--danger,#d2563f)' : v.pct < 0 ? 'var(--positive,#2e9e5b)' : 'var(--on-dim)' }}>
                          {v.isNew ? 'new' : v.pct == null ? '—' : `${v.pct > 0 ? '▲' : '▼'} ${Math.abs(v.pct).toFixed(Math.abs(v.pct) < 10 ? 1 : 0)}%`}
                        </div>
                      </div>
                    </button>
                    {open && (
                      <div style={{ padding: '0 4px 14px' }}>
                        <VendorMonthsChart months={v.months} M={M} onCanvas />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

// ─── Bills due — outstanding payables (excludes card & pay-on-delivery), plus a
//     credit-card-spend-this-month section. Reuses the Today "outstanding" data.
function BillsDue({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false }); return; }
        const db = window.kanjo.db;
        const today = localToday();
        const [unpaid, card, profile] = await Promise.all([
          window.kanjo.cashflow.payments.unpaidSummary(today),
          window.kanjo.cashflow.payments.cardSpendThisMonth(today),
          db.userProfile.get(claims.sub).catch(() => null),
        ]);
        if (!cancelled) setSt({ loading: false, unpaid, card, currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) {
        if (!cancelled) setSt({ loading: false, error: String(e?.message || e) });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (st.loading) return (
    <KShell dark={dark} tab="receipts" persona={persona}>
      <KHeader back big title="Bills due" />
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>Loading…</div>
    </KShell>
  );

  const M = (c) => fmtMoneyExact(c || 0, st.currency, st.locale);
  const unpaid = st.unpaid || { totalCents: 0, count: 0, vendors: [] };
  const card = st.card || { totalCents: 0, vendors: [] };

  return (
    <KShell dark={dark} tab="receipts" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back big title="Bills due" subtitle="Outstanding — excludes card & pay-on-delivery" />
        <div style={{ padding: '0 16px' }}>
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}

          {/* Outstanding bills (statement + on-account vendors) */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '14px 16px', borderBottom: unpaid.vendors.length ? '1px solid var(--rule)' : 'none' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Outstanding{unpaid.count ? ` · ${unpaid.count}` : ''}</div>
              <div className="mono" style={{ fontSize: 18, fontWeight: 700 }}>{M(unpaid.totalCents)}</div>
            </div>
            {unpaid.vendors.length === 0 && (
              <div style={{ padding: '18px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No bills due — you're all caught up.</div>
            )}
            {unpaid.vendors.map((v, i, a) => (
              <KLink key={v.vendorId} to={`/buy/vendor/detail?id=${encodeURIComponent(v.vendorId)}`} asButton>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                  <div style={{ minWidth: 0, paddingRight: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{v.vendorName}</div>
                    <div style={{ fontSize: 12, color: v.unsetPolicy ? 'var(--warning)' : 'var(--ink-3)', marginTop: 1 }}>
                      {v.unsetPolicy
                        ? `${v.count} invoice${v.count === 1 ? '' : 's'} · set payment terms →`
                        : v.nextPayIso
                          ? `${v.count} invoice${v.count === 1 ? '' : 's'} · pay by ${v.nextPayIso}`
                          : `${v.count} invoice${v.count === 1 ? '' : 's'} · outstanding`}
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{M(v.totalCents)}</div>
                </div>
              </KLink>
            ))}
          </div>

          {/* Credit card spend this month — paid via card, but the cycle is long */}
          {card.vendors.length > 0 && (
            <>
              <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Credit card spend · this month</div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--rule)' }}>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Charged this month</div>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700 }}>{M(card.totalCents)}</div>
                </div>
                {card.vendors.map((v, i, a) => (
                  <KLink key={v.vendorId} to={`/buy/vendor/detail?id=${encodeURIComponent(v.vendorId)}`} asButton>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                      <div style={{ minWidth: 0, paddingRight: 12 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{v.vendorName}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{v.count} receipt{v.count === 1 ? '' : 's'}</div>
                      </div>
                      <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{M(v.totalCents)}</div>
                    </div>
                  </KLink>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

Object.assign(window, { ReceiptsMenu, BuyMenu, ReportsMenu, RecentReceipts, InvoiceDetail, BillsDue, PriceChanges, PriceCheck, VendorSpend, MenuTile, MenuGrid, useHashQuery, setHashQuery });
